import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Globe, FileText, Database, Edit3, CheckCircle, Clock,
  XCircle, Forward, Plus, Search, ChevronDown, ChevronUp,
  RefreshCw, Eye, History, Trash2, UserCheck, AlertCircle, Mail,
  Bell, X, Info, AlertTriangle, CheckSquare, Snowflake,
  ThermometerSun, Flame, Zap, Merge, MoreHorizontal, Folder, Tags
} from 'lucide-react';
import { templateUtils } from '../utils/templateUtils';
import { getProducerName } from '../utils/userUtils';
import { campaignUtils } from '../utils/campaignUtils';
import CampaignModal from './CampaignModal';
import ContactManagementPanel from './ContactManagementPanel';
import ContactProfileModal from './ContactProfileModal';
import EmailTemplateFormFields from './EmailTemplateFormFields';
import TagFolderManager from './TagFolderManager';
import FolderManager from './FolderManager';
import NotesModal from './NotesModal';
import BouncedContactsModal from './BouncedContactsModal';
import { useAuth } from '../context/AuthContext';
import { getDefaultProducerFilter } from '../utils/userUtils';
import { LinkedInTargetingSetup } from './LinkedInTargetingSetup';
import CompanyCreateModal from './CompanyCreateModal';
import CompanyDetailsModal from './CompanyDetailsModal';
import FolderPerformancePanel from './FolderPerformancePanel';

const parseContactTagsValue = (tagsValue) => {
  if (!tagsValue) return [];
  if (Array.isArray(tagsValue)) return tagsValue.map(tag => String(tag).trim()).filter(Boolean);
  try {
    const parsed = JSON.parse(tagsValue);
    if (Array.isArray(parsed)) return parsed.map(tag => String(tag).trim()).filter(Boolean);
  } catch (error) {
    // Use the legacy comma-separated format below.
  }
  return String(tagsValue).split(',').map(tag => tag.trim()).filter(Boolean);
};

// Utility function to convert HTML to plain text for previews
const htmlToPlainText = (html) => {
  if (!html) return '';

  // First decode HTML entities
  let decoded = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Convert common HTML elements to readable text
  decoded = decoded
    .replace(/<br\s*\/?>/gi, ' ') // Line breaks to spaces
    .replace(/<\/p>/gi, ' ') // Paragraph endings to spaces
    .replace(/<li[^>]*>/gi, '• ') // List items to bullets
    .replace(/<\/li>/gi, ' ') // List item endings to spaces
    .replace(/<[^>]+>/g, '') // Remove all remaining HTML tags
    .replace(/\s+/g, ' ') // Multiple spaces to single space
    .trim();

  return decoded;
};

const formatMetricCount = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return '0';
  return numeric >= 1000 ? numeric.toLocaleString() : `${numeric}`;
};

const formatMetricRate = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric) || numeric <= 0) return '0%';
  const percentage = numeric * 100;
  return percentage >= 10 ? `${Math.round(percentage)}%` : `${percentage.toFixed(1)}%`;
};

const createCompanyDetailsModalState = () => ({
  isOpen: false,
  companyId: null,
  loading: false,
  data: null,
  editing: false,
  saving: false,
  error: null,
  values: {
    name: '',
    website: '',
    domain: '',
    phone: '',
    email: '',
    location: '',
    country: '',
    state: '',
    zip_code: '',
    address: ''
  }
});

const StepMetric = ({ label, value, emphasize = false }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${emphasize ? 'text-blue-600' : 'text-gray-700'}`}>
    <span className="uppercase tracking-wide text-gray-400">{label}</span>
    <span className="text-gray-900">{value}</span>
  </span>
);

export const ProducerDashboard = ({ currentProducer, stats, campaigns, producerColors }) => {
  const [producerStats, setProducerStats] = useState({
    totalContacts: 0,
    emailsSent: 0,
    responseRate: 0,
    notInterested: 0,
    oooContacts: 0,
    forwardedContacts: 0
  });
  const [producerCampaigns, setProducerCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProducerStats = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`/api/contact-management/producer-stats/${currentProducer.id}`);

      if (response.data.success) {
        setProducerStats(response.data.stats);
        setProducerCampaigns(response.data.campaigns);
      }

    } catch (error) {
      console.error('Failed to load stats:', error);
      // Could add fallback logic here if needed
    } finally {
      setLoading(false);
    }
  }, [currentProducer, stats]);

  useEffect(() => {
    if (currentProducer) {
      loadProducerStats();
    }
  }, [currentProducer, loadProducerStats]);

  // Format campaign for display
  const formatCampaign = (campaign) => {
    return {
      id: campaign.id,
      name: campaign.name,
      activeContacts: campaign.total_recipients || 0,
      frequency: campaign.frequency_type || 'manual',
      nextRun: campaign.next_run || 'Not scheduled',
      status: campaign.status
    };
  };

  const derivedTeamId = currentProducer?.team_id ?? currentProducer?.teamId ?? null;
  const isZohoEnabledTeam = Number(derivedTeamId) === 1;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">{currentProducer?.username || currentProducer?.display_name || 'User'} Dashboard</h2>

      </div>

      <div className="grid grid-cols-6 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">Total Contacts</p>
          <p className="text-xl font-semibold text-gray-900">
            {loading ? '...' : producerStats.totalContacts}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">Emails Sent</p>
          <p className="text-xl font-semibold text-gray-900">
            {loading ? '...' : producerStats.emailsSent}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">Response Rate</p>
          <p className="text-xl font-semibold text-green-600">
            {loading ? '...' : `${producerStats.responseRate}%`}
          </p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">Not Interested</p>
          <p className="text-xl font-semibold text-red-600">
            {loading ? '...' : producerStats.notInterested}
          </p>
          <p className="text-xs text-gray-500 mt-1">To be deleted</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">OOO</p>
          <p className="text-xl font-semibold text-orange-600">
            {loading ? '...' : producerStats.oooContacts}
          </p>
          <p className="text-xs text-gray-500 mt-1">Paused</p>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <p className="text-xs text-gray-600">Forwarded</p>
          <p className="text-xl font-semibold text-blue-600">
            {loading ? '...' : producerStats.forwardedContacts}
          </p>
          <p className="text-xs text-gray-500 mt-1">New leads</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-4">Active Campaigns</h3>
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              <p className="text-gray-500">Loading campaigns...</p>
            </div>
          ) : producerCampaigns.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No active campaigns</p>
            </div>
          ) : (
            producerCampaigns.map(campaign => {
              const formattedCampaign = formatCampaign(campaign);
              return (
                <div key={formattedCampaign.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{formattedCampaign.name}</p>
                    <p className="text-sm text-gray-600">
                      {formattedCampaign.activeContacts} contacts • {formattedCampaign.frequency}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">Next Run</p>
                    <p className="text-xs text-gray-600">{formattedCampaign.nextRun}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export const LeadSources = () => {
  const { user } = useAuth();
  const [showLinkedInSetup, setShowLinkedInSetup] = useState(false);
  const [showScrapingHistory, setShowScrapingHistory] = useState(false);
  const [leadSourceStats, setLeadSourceStats] = useState({
    linkedInScraping: { total: 0, thisWeek: 0 },
    websiteForms: { total: 0, quoteRequests: 0, meetingRequests: 0, copyrightRequests: 0 },
    existingZoho: { total: 0 },
    totalContacts: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeadSourceStats();
  }, []);

  const loadLeadSourceStats = async () => {
    try {
      const response = await axios.get('/api/contact-management/lead-source-stats');
      if (response.data.success) {
        setLeadSourceStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load lead source stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const derivedTeamId = user?.teamId ?? user?.team_id ?? null;
  const isZohoEnabledTeam = Number(derivedTeamId) === 1;

  if (showLinkedInSetup) {
    return <LinkedInTargetingSetup onBack={() => setShowLinkedInSetup(false)} />;
  }

  if (showScrapingHistory) {
    return <ApolloScrapingHistory onBack={() => setShowScrapingHistory(false)} />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Linkedin Scraping</h2>

      <div className="grid grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">LinkedIn Scraping</h3>
            <Globe className="text-blue-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-blue-600 mb-2">
            {loading ? '...' : leadSourceStats.linkedInScraping.total}
          </p>
          <p className="text-sm text-gray-600">New leads this week</p>
          <p className="text-xs text-gray-500">
            Total: {loading ? '...' : leadSourceStats.linkedInScraping.thisWeek}
          </p>
          <div className="mt-4 space-y-2">
            <button
              onClick={() => setShowLinkedInSetup(true)}
              className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
            >
              Start Scraping
            </button>
            <button
              onClick={() => setShowScrapingHistory(true)}
              className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
            >
              <History size={16} />
              View Scraping History
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Website Forms</h3>
            <FileText className="text-green-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-green-600 mb-2">
            {loading ? '...' : leadSourceStats.websiteForms.total}
          </p>
          <p className="text-sm text-gray-600">Form submissions</p>
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs">
              <span>Quote Requests</span>
              <span className="font-medium">
                {loading ? '...' : leadSourceStats.websiteForms.quoteRequests}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span>Playbook Requests</span>
              <span className="font-medium">
                {loading ? '...' : leadSourceStats.websiteForms.meetingRequests}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span>Document Requests</span>
              <span className="font-medium">
                {loading ? '...' : leadSourceStats.websiteForms.copyrightRequests}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Existing Zoho</h3>
            <Database className="text-purple-500" size={24} />
          </div>
          <p className="text-3xl font-bold text-purple-600 mb-2">
            {loading ? '...' : leadSourceStats.existingZoho.total}
          </p>
          <p className="text-sm text-gray-600">Available for campaigns</p>
          <button className="mt-4 w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700">
            Import Contacts
          </button>
        </div>
      </div>

      {isZohoEnabledTeam && (
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="font-semibold text-gray-900 mb-4">Producer Assignment Rules</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="font-medium text-blue-900">Kelly US</p>
              <p className="text-sm text-blue-700 mt-1">US East Coast leads</p>
              <p className="text-xs text-blue-600 mt-2">Auto-assigned: EST timezone</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="font-medium text-green-900">Angela US</p>
              <p className="text-sm text-green-700 mt-1">US West Coast leads</p>
              <p className="text-xs text-green-600 mt-2">Auto-assigned: PST timezone</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="font-medium text-purple-900">Chelsie UK</p>
              <p className="text-sm text-purple-700 mt-1">UK & Europe leads</p>
              <p className="text-xs text-purple-600 mt-2">Auto-assigned: GMT/BST timezone</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


export const ApolloScrapingHistory = ({ onBack }) => {
  const [scrapingJobs, setScrapingJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobLeads, setJobLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchScrapingHistory();
  }, []);

  const fetchScrapingHistory = async () => {
    try {
      const response = await fetch('/api/apollo/search-history', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const result = await response.json();
      if (response.ok) {
        setScrapingJobs(result.jobs);
      }
    } catch (error) {
      console.error('Failed to fetch scraping history:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobLeads = async (jobId) => {
    try {
      const response = await fetch(`/api/apollo/scraping-leads/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.leads && Array.isArray(result.leads)) {
        setJobLeads(result.leads);
        setSelectedJob(jobId);
        console.log(`Successfully loaded ${result.leads.length} leads for job ${jobId}`);
      } else {
        console.warn('No leads found or invalid response format:', result);
        setJobLeads([]);
        setSelectedJob(jobId);
      }
    } catch (error) {
      console.error('Failed to fetch job leads:', error);
      alert(`Failed to load leads: ${error.message}`);
    }
  };


  if (selectedJob) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedJob(null)}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            ← Back to History
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Scraping Job #{selectedJob} - Leads</h2>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">
              {jobLeads.length} Total Leads
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobLeads.map(lead => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {lead.first_name} {lead.last_name}
                        </div>
                        <div className="text-xs text-gray-400">{lead.email}</div>
                        {lead.phone && <div className="text-xs text-gray-400">{lead.phone}</div>}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {lead.company}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {lead.job_title}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {lead.location}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          ← Back
        </button>
        <h2 className="text-2xl font-bold text-gray-900">Apollo Scraping History</h2>
        <button
          onClick={fetchScrapingHistory}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
          <p>Loading scraping history...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-900">Recent Scraping Jobs</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Results</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Leads</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scrapingJobs.map(job => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap font-mono text-sm">
                      #{job.id}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${job.status === 'completed' ? 'bg-green-100 text-green-700' :
                        job.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                        {job.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {job.results_count || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {job.new_leads_count || 0}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-gray-500">
                      {new Date(job.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => fetchJobLeads(job.id)}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Eye size={14} />
                          View Leads
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


export const EmailCampaigns = ({
  producerColors,
  gmailStatus,
  initialCampaign = null,
  onInitialCampaignHandled
}) => {

  const initialCampaignRef = useRef(initialCampaign);
  const initialCampaignHandledRef = useRef(onInitialCampaignHandled);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [managedCampaignRecipients, setManagedCampaignRecipients] = useState(null);
  const [showFullUI, setShowFullUI] = useState(true);
  const [sortColumn, setSortColumn] = useState('clicks');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [roundFilter, setRoundFilter] = useState('all');
  const [currentStatusFilter, setCurrentStatusFilter] = useState(null);

  // Folder management state
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(null);
  const [folders, setFolders] = useState([]); // Keep for dropdown references

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [folderCounts, setFolderCounts] = useState({});
  const [campaignSearch, setCampaignSearch] = useState(initialCampaign?.name || '');
  const PAGE_SIZE = 20;

  // Load campaigns and templates on component mount
  useEffect(() => {
    const focusedCampaign = initialCampaignRef.current;
    const initialSearch = focusedCampaign?.name || '';
    loadCampaigns(1, null, initialSearch).then(() => {
      if (focusedCampaign?.id) {
        scrollToCampaign(focusedCampaign.id);
        initialCampaignHandledRef.current?.();
      }
    });
    loadTemplates();
    loadFolders();

    // Listen for campaign refresh events
    const handleCampaignsRefresh = (event) => {
      console.log('Received campaigns refresh event, updating UI...');
      if (event.detail?.campaigns) {
        setCampaigns(event.detail.campaigns);
        if (event.detail.total != null) setTotalCampaigns(event.detail.total);
        if (event.detail.folderCounts) setFolderCounts(event.detail.folderCounts);
      } else {
        // Fallback: reload campaigns if no data provided
        loadCampaigns(currentPage);
      }
    };

    // Listen for folder refresh events
    const handleFoldersRefresh = (event) => {
      console.log('Received folders refresh event, updating UI...');
      loadFolders();
    };

    window.addEventListener('campaignsRefresh', handleCampaignsRefresh);
    window.addEventListener('foldersRefresh', handleFoldersRefresh);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('campaignsRefresh', handleCampaignsRefresh);
      window.removeEventListener('foldersRefresh', handleFoldersRefresh);
    };
  }, []);

  // Debug: Track showContactPanel state changes
  useEffect(() => {
    console.log('useEffect triggered: showContactPanel changed to:', showContactPanel);
  }, [showContactPanel]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.folder-dropdown-container')) {
        setShowFolderDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // Function to scroll to a specific campaign
  const scrollToCampaign = (campaignId) => {
    if (campaignId) {
      console.log('Scrolling to campaign:', campaignId);

      setTimeout(() => {
        const campaignElement = document.getElementById(`campaign-${campaignId}`);
        if (campaignElement) {
          console.log('Scrolling to campaign element:', campaignId);
          campaignElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 200); // Small delay to ensure DOM is updated
    }
  };

  const loadCampaigns = async (page = currentPage, folderId = selectedFolderId, search = campaignSearch) => {
    try {
      setLoading(true);
      const { campaigns, total, folderCounts } = await campaignUtils.loadCampaigns({ page, limit: PAGE_SIZE, folderId, search });
      setCampaigns(campaigns);
      setTotalCampaigns(total);
      setFolderCounts(folderCounts);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
      alert('Failed to load email campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFolderCampaign = campaign => {
    const campaignName = campaign?.name || '';
    if (!campaign?.id) return;
    setCampaignSearch(campaignName);
    loadCampaigns(1, selectedFolderId, campaignName)
      .then(() => scrollToCampaign(campaign.id));
  };

  const loadTemplates = async () => {
    try {
      const templates = await templateUtils.loadTemplates();
      setTemplates(templates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const handleTemplateBodyUpdated = async updatedTemplate => {
    const matchesTemplate = template => String(template.template_id ?? template.id) === String(updatedTemplate.id);
    const updateCampaignTemplate = template => matchesTemplate(template) ? {
      ...template,
      template_name: updatedTemplate.name,
      subject: updatedTemplate.subject,
      body: updatedTemplate.body,
      link_previews: updatedTemplate.link_previews,
      attachments: updatedTemplate.attachments,
      template_updated_at: updatedTemplate.updated_at
    } : template;

    setTemplates(currentTemplates => currentTemplates.map(template => (
      matchesTemplate(template) ? { ...template, ...updatedTemplate } : template
    )));
    setCampaigns(currentCampaigns => currentCampaigns.map(campaign => ({
      ...campaign,
      templates: campaign.templates?.map(updateCampaignTemplate)
    })));
    setEditingCampaign(currentCampaign => currentCampaign ? {
      ...currentCampaign,
      templates: currentCampaign.templates?.map(updateCampaignTemplate)
    } : currentCampaign);

    await Promise.all([loadTemplates(), loadCampaigns()]);
  };

  // Load folders for dropdown references
  const loadFolders = async () => {
    try {
      const response = await axios.get('/api/folders?folder_type=campaign');
      setFolders(response.data || []);
    } catch (error) {
      console.error('Error loading folders:', error);
    }
  };

  // Move campaign to folder
  const handleMoveCampaignToFolder = async (campaignId, folderId) => {
    try {
      const response = await axios.patch(`/api/folders/move-item`, {
        item_id: campaignId,
        item_type: 'campaign',
        folder_id: folderId
      });

      if (response.data.success) {
        // Refresh campaigns to update the UI
        loadCampaigns();
        setShowFolderDropdown(null);
      }
    } catch (error) {
      console.error('Error moving campaign to folder:', error);
      alert('Failed to move campaign to folder');
    }
  };

  const handleFolderCampaignStatus = async (folder, action) => {
    const isPause = action === 'pause';
    const actionLabel = isPause ? 'pause' : 'resume';
    const confirmation = isPause
      ? `Pause all active campaigns in "${folder.name}"? Draft, completed, and cancelled campaigns will not change.`
      : `Resume all paused campaigns in "${folder.name}"?`;

    if (!window.confirm(confirmation)) return;

    if (!isPause && !gmailStatus?.hasAccounts) {
      alert('Please connect a Gmail account before resuming campaigns. Go to Settings to connect a Gmail account.');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(`/api/email-campaigns/folders/${folder.id}/status`, {
        action
      });

      if (response.data.success) {
        const updatedCount = response.data.updated_count || 0;
        const skippedCount = response.data.skipped_locked_count || 0;
        let message = updatedCount === 0
          ? `No campaigns in "${folder.name}" were available to ${actionLabel}.`
          : `${updatedCount} campaign${updatedCount === 1 ? '' : 's'} ${isPause ? 'paused' : 'resumed'} in "${folder.name}".`;

        if (skippedCount > 0) {
          message += ` ${skippedCount} campaign${skippedCount === 1 ? ' was' : 's were'} still processing and could not be resumed.`;
        }

        alert(message);
        await loadCampaigns();
      }
    } catch (error) {
      console.error(`Failed to ${actionLabel} folder campaigns:`, error);
      alert(`Failed to ${actionLabel} folder campaigns: ` + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Sorting function for campaign recipients table
  const handleSort = (column) => {
    let newDirection;
    if (sortColumn === column) {
      // Toggle direction for the same column
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      // Default direction for new column
      newDirection = column === 'clicks' ? 'desc' : 'asc';
    }
    setSortColumn(column);
    setSortDirection(newDirection);
  };

  // Filter and sort the campaign recipients
  const filteredAndSortedRecipients = React.useMemo(() => {
    // First apply round filter
    let filtered = campaignRecipients;
    if (roundFilter !== 'all') {
      const targetRound = parseInt(roundFilter);
      filtered = campaignRecipients.filter(recipient =>
        (recipient.round_number || 0) === targetRound
      );
    }

    // Then apply sorting if specified
    if (!sortColumn) return filtered;

    return [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'status':
          aVal = a.status || 'pending';
          bVal = b.status || 'pending';
          break;
        case 'engagement':
          // Sort by engagement_score directly from recipient data
          aVal = a.engagement_score || 0;
          bVal = b.engagement_score || 0;
          break;
        case 'leadType':
          // Sort by recipient_type (cold_leads, warm_prospects, active_clients)
          aVal = a.recipient_type || 'cold_leads';
          bVal = b.recipient_type || 'cold_leads';
          break;
        case 'opens':
          aVal = a.open_count || 0;
          bVal = b.open_count || 0;
          break;
        case 'clicks':
          aVal = a.click_count || 0;
          bVal = b.click_count || 0;
          break;
        case 'round':
          aVal = a.round_number || 0;
          bVal = b.round_number || 0;
          break;
        case 'schedule':
          aVal = a.next_scheduled_at || a.scheduled_at || '';
          bVal = b.next_scheduled_at || b.scheduled_at || '';
          break;
        case 'pause':
          // Sort by recipient_status (paused, active, completed)
          aVal = a.recipient_status || 'active';
          bVal = b.recipient_status || 'active';
          break;
        default:
          return 0;
      }

      // Handle numeric sorting
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Handle string/date sorting
      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return sortDirection === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [campaignRecipients, sortColumn, sortDirection, roundFilter]);

  // Clear selection and round filter when changing campaigns
  useEffect(() => {
    setSelectedRecipients([]);
    setRoundFilter('all');
  }, [selectedCampaign]);

  // Selection handlers
  const handleSelectRecipient = (recipientId) => {
    setSelectedRecipients(prev =>
      prev.includes(recipientId)
        ? prev.filter(id => id !== recipientId)
        : [...prev, recipientId]
    );
  };

  const handleSelectAll = () => {
    setSelectedRecipients(prev =>
      prev.length === filteredAndSortedRecipients.length ? [] : filteredAndSortedRecipients.map(r => r.id)
    );
  };

  // Bulk action handler
  const handleBulkAction = async (action) => {
    if (selectedRecipients.length === 0) return;

    // Validate transitions based on current recipient types
    if (action === 'cold_to_warm' || action === 'warm_to_active') {
      const selectedRecipientObjects = filteredAndSortedRecipients.filter(r => selectedRecipients.includes(r.id));

      if (action === 'cold_to_warm') {
        const nonColdRecipients = selectedRecipientObjects.filter(r => r.recipient_type !== 'cold_leads');
        if (nonColdRecipients.length > 0) {
          alert(`Cannot promote to Warm: ${nonColdRecipients.length} selected recipients are not Cold leads. Only Cold leads can be promoted to Warm.`);
          return;
        }
      }

      if (action === 'warm_to_active') {
        const nonWarmRecipients = selectedRecipientObjects.filter(r => r.recipient_type !== 'warm_prospects');
        if (nonWarmRecipients.length > 0) {
          alert(`Cannot promote to Active: ${nonWarmRecipients.length} selected recipients are not Warm prospects. Only Warm prospects can be promoted to Active.`);
          return;
        }
      }
    }

    const confirmMessage = {
      pause: `Are you sure you want to pause ${selectedRecipients.length} recipients?`,
      unpause: `Are you sure you want to unpause ${selectedRecipients.length} recipients?`,
      cold_to_warm: `Are you sure you want to promote ${selectedRecipients.length} Cold leads to Warm prospects?`,
      warm_to_active: `Are you sure you want to promote ${selectedRecipients.length} Warm prospects to Active clients?`
    };

    if (!window.confirm(confirmMessage[action])) return;

    try {
      setLoading(true);

      let updateData = {};

      if (action === 'pause' || action === 'unpause') {
        // Handle pause/unpause via email campaigns route
        const updateData = { recipient_status: action === 'pause' ? 'paused' : 'active' };
        const promises = selectedRecipients.map(recipientId =>
          axios.put(`/api/email-campaigns/recipients/${recipientId}`, updateData)
        );
        await Promise.all(promises);
      } else if (action === 'cold_to_warm' || action === 'warm_to_active') {
        // Handle type promotion via contact management bulk promote route
        const selectedRecipientObjects = filteredAndSortedRecipients.filter(r => selectedRecipients.includes(r.id));

        // Get the contact IDs and validate types
        const contactIds = selectedRecipientObjects.map(r => r.recipient_id).filter(id => id);

        if (contactIds.length === 0) {
          alert('No valid contact IDs found for selected recipients');
          setLoading(false);
          return;
        }

        let fromType, toType;
        if (action === 'cold_to_warm') {
          fromType = 'cold';
          toType = 'warm';
        } else {
          fromType = 'warm';
          toType = 'active';
        }

        // Use bulk promote endpoint
        await axios.put('/api/contact-management/contacts/bulk-promote', {
          contactIds,
          fromType,
          toType,
          notes: `Bulk promoted via campaign management on ${new Date().toISOString()}`
        });
      }

      // Reload campaign recipients to show updated data
      const recipientsResponse = await axios.get(`/api/email-campaigns/${selectedCampaign.id}/recipients`);
      if (recipientsResponse.data.success) {
        setCampaignRecipients(recipientsResponse.data.recipients);
      }

      // Clear selection
      setSelectedRecipients([]);

      alert(`Successfully updated ${selectedRecipients.length} recipients!`);
    } catch (error) {
      console.error('Failed to update recipients:', error);
      alert('Failed to update recipients: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEditCampaign = (campaign) => {
    setManagedCampaignRecipients(null);
    setEditingCampaign(campaign);
    setShowEditModal(true);
  };



  const handlePauseCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to pause this campaign?')) return;

    try {
      setLoading(true);
      const response = await axios.put(`/api/email-campaigns/${campaignId}/pause`, {
        action: 'pause'
      });

      if (response.data.success) {
        alert('Campaign paused successfully!');
        loadCampaigns(); // Reload campaigns
      }
    } catch (error) {
      console.error('Failed to pause campaign:', error);
      alert('Failed to pause campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleResumeCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to resume this campaign?')) return;

    // Check if Gmail account is connected
    if (!gmailStatus?.hasAccounts) {
      alert('Please connect a Gmail account before resuming a campaign. Go to Settings to connect your Gmail account.');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put(`/api/email-campaigns/${campaignId}/pause`, {
        action: 'resume'
      });

      if (response.data.success) {
        alert('Campaign resumed successfully!');
        loadCampaigns(); // Reload campaigns
      }
    } catch (error) {
      console.error('Failed to resume campaign:', error);
      alert('Failed to resume campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to permanently delete this campaign? This action cannot be undone.')) return;

    try {
      setLoading(true);
      const response = await axios.delete(`/api/email-campaigns/${campaignId}`);

      if (response.data.success) {
        alert('Campaign deleted successfully!');
        loadCampaigns(); // Reload campaigns
      }
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      alert('Failed to delete campaign: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleViewCampaignDetails = async (campaign, statusFilter = null, showFullUI = true) => {
    try {
      let campaignId = campaign.id;
      setLoadingDetails(true);
      setCurrentStatusFilter(statusFilter);

      // Load campaign details only if showing full UI
      if (showFullUI) {
        const campaignResponse = await axios.get(`/api/email-campaigns/${campaignId}`);
        if (campaignResponse.data.success) {
          setSelectedCampaign(campaignResponse.data.campaign);
        }
      } else {
        // For limited UI, create a minimal campaign object with just the ID
        setSelectedCampaign(campaign);
      }



      // Load campaign recipients (always fetch all, then filter client-side)
      const recipientsResponse = await axios.get(`/api/email-campaigns/${campaignId}/recipients`);
      if (recipientsResponse.data.success) {
        let recipients = recipientsResponse.data.recipients;

        // Apply client-side filtering if statusFilter is specified
        if (statusFilter) {
          recipients = recipients.filter(recipient => {
            // Map the filter values to actual recipient status values
            switch (statusFilter) {
              case 'sent':
                return recipient.status !== 'pending' && recipient.status !== 'failed';
              case 'bounced':
                return recipient.status === 'bounced' || recipient.resent_count > 0;
              case 'replied':
                // Check for various reply-related statuses
                return recipient.status === 'replied' ||
                  recipient.status === 'not_interested' ||
                  recipient.status === 'out_of_office' ||
                  recipient.status === 'forwarded';
              case 'open':
                // Filter recipients who have opened emails (open_count > 0)
                return (recipient.open_count || 0) > 0;
              case 'click':
                // Filter recipients who have clicked links (click_count > 0)
                return (recipient.click_count || 0) > 0;
              default:
                return true;
            }
          });
        }

        setCampaignRecipients(recipients);
      }

      // Set whether to show full UI or limited UI
      setShowFullUI(showFullUI);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Failed to load campaign details:', error);
      alert('Failed to load campaign details: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700';
      case 'scheduled': return 'bg-blue-100 text-blue-700';
      case 'sending': return 'bg-yellow-100 text-yellow-700';
      case 'sent': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'paused': return 'bg-orange-100 text-orange-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTargetTypeLabel = (targetType) => {
    targetType = targetType.split('|')[0]; // Get the first type if multiple are specified
    switch (targetType) {
      case 'cold_leads': return 'Cold Leads';
      case 'warm_prospects': return 'Warm Prospects';
      case 'active_clients': return 'Active Clients';
      case 'mixed': return 'Mixed';
      default: return targetType;
    }
  };

  const getRecipientStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'bg-blue-100 text-blue-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'opened': return 'bg-purple-100 text-purple-800';
      case 'clicked': return 'bg-indigo-100 text-indigo-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'bounced': return 'bg-red-100 text-red-800';
      case 'not_interested': return 'bg-yellow-100 text-yellow-800';
      case 'out_of_office': return 'bg-orange-100 text-orange-800';
      case 'forwarded': return 'bg-cyan-100 text-cyan-800';
      case 'pending':
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRecipientStatusColor2 = (recipientStatus) => {
    switch (recipientStatus) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'paused': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getEngagementTierInfo = (tier) => {
    switch (tier) {
      case 'super_hot':
        return { icon: Zap, color: 'text-red-600', bgColor: 'bg-red-50', title: 'Super Hot' };
      case 'hot':
        return { icon: Flame, color: 'text-orange-600', bgColor: 'bg-orange-50', title: 'Hot' };
      case 'warm':
        return { icon: ThermometerSun, color: 'text-yellow-600', bgColor: 'bg-yellow-50', title: 'Warm' };
      case 'cold':
      default:
        return { icon: Snowflake, color: 'text-blue-400', bgColor: 'bg-blue-50', title: 'Cold' };
    }
  };

  const getEngagementInfo = (recipient) => {
    const campaignScore = recipient.engagement_score || 0;
    const openCount = recipient.open_count || 0;
    const clickCount = recipient.click_count || 0;
    const responseCount = recipient.response_count || 0;

    // Use existing engagement_tier from campaign_recipients or calculate based on campaign score
    const tier = recipient.engagement_tier || 'cold';
    const tierInfo = getEngagementTierInfo(tier);

    return {
      campaignScore,
      openCount,
      clickCount,
      responseCount,
      tier,
      tierInfo
    };
  };

  const toggleRecipientStatus = async (recipientId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'paused' ? 'active' : 'paused';
      const response = await axios.put(`/api/email-campaigns/recipients/${recipientId}`, {
        recipient_status: newStatus
      });

      if (response.data.success) {
        // Reload campaign recipients to show the updated status
        const recipientsResponse = await axios.get(`/api/email-campaigns/${selectedCampaign.id}/recipients`);
        if (recipientsResponse.data.success) {
          setCampaignRecipients(recipientsResponse.data.recipients);
        }
      }
    } catch (error) {
      console.error('Failed to update recipient status:', error);
      alert('Failed to update recipient status: ' + (error.response?.data?.error || error.message));
    }
  };

  // Use the enhanced CampaignModal component
  if (showCreateModal) {
    return (
      <div>
        <CampaignModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          templates={templates}
          gmailStatus={gmailStatus}
          loadCampaigns={loadCampaigns}
          setShowContactPanel={setShowContactPanel}
          title="Create New Campaign"
        />
      </div>
    );
  }

  if (showEditModal && editingCampaign) {
    return (
      <div>
        <CampaignModal
          isOpen={showEditModal}
          onClose={() => {
            const campaignId = editingCampaign?.id;
            setShowEditModal(false);
            setEditingCampaign(null);
            setManagedCampaignRecipients(null);
            scrollToCampaign(campaignId);
          }}
          editingCampaign={editingCampaign}
          managedRecipients={managedCampaignRecipients}
          templates={templates}
          gmailStatus={gmailStatus}
          loadCampaigns={loadCampaigns}
          onTemplateBodyUpdated={handleTemplateBodyUpdated}
          setShowContactPanel={setShowContactPanel}
        />
        {/* Contact Management Panel - rendered as overlay when showContactPanel is true */}
        {showContactPanel && (
          <ContactManagementPanel
            isOpen={true}
            onClose={() => {
              console.log('ContactManagementPanel onClose called');
              setShowContactPanel(false);
            }}
            onRecipientsChanged={(recipients) => {
              setManagedCampaignRecipients(recipients);
              loadCampaigns(currentPage);
            }}
            selectedCampaign={editingCampaign}
          />
        )}
      </div>
    );
  }

  // Campaign Details Modal
  if (showDetailsModal && selectedCampaign) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-7xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Campaign Details: {selectedCampaign.name}</h2>
            <button
              onClick={() => {
                const campaignId = selectedCampaign?.id;
                setShowDetailsModal(false);
                setSelectedCampaign(null);
                setCampaignRecipients([]);
                scrollToCampaign(campaignId);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-gray-900">
                  Recipients ({filteredAndSortedRecipients.length}{roundFilter !== 'all' ? ` of ${campaignRecipients.length}` : ''})
                  {currentStatusFilter === 'sent' && (() => {
                    // Group ALL sent recipients by round_number (not just filtered ones) to show complete count
                    // When round_number is 2, it means recipient got round 1 AND round 2
                    const sentByRound = campaignRecipients
                      .filter(recipient => recipient.status === 'sent')
                      .reduce((acc, recipient) => {
                        const maxRound = recipient.round_number || 0;
                        // Count this recipient for each round they went through
                        for (let round = 1; round <= maxRound; round++) {
                          acc[round] = (acc[round] || 0) + 1;
                        }
                        return acc;
                      }, {});

                    const sortedRounds = Object.entries(sentByRound).sort(([a], [b]) => parseInt(a) - parseInt(b));

                    // Only show round breakdown when there are multiple rounds
                    /*if ((selectedCampaign.current_round || 0) < 1) {
                      return null;
                    }*/

                    // Calculate resent count: campaign.sent_count - total count by recipients
                    const totalRoundCounts = sortedRounds.reduce((sum, [round, count]) => sum + count, 0);
                    const resentCount = (selectedCampaign.sent_count || 0) - totalRoundCounts;
                    console.log('Resent calculation:', selectedCampaign.sent_count, 'sent_count -', totalRoundCounts, 'round_counts =', resentCount);
                    return sortedRounds.length > 0 ? (
                      <div className="ml-4 flex flex-wrap items-center gap-2 mt-2">
                        <span className="text-xs font-medium text-gray-600">Sent by Round:</span>
                        {sortedRounds.map(([round, count]) => (
                          <span
                            key={round}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200"
                          >
                            Round {round}: <span className="ml-1 font-semibold">{count}</span>
                          </span>
                        ))}
                        {resentCount > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                            Resent: <span className="ml-1 font-semibold">{resentCount}</span>
                          </span>
                        )}
                      </div>
                    ) : null;
                  })()}
                </h3>
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-gray-700">Round:</label>
                  <select
                    value={roundFilter}
                    onChange={(e) => setRoundFilter(e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm bg-white"
                  >
                    <option value="all">All Rounds</option>
                    {(() => {
                      const rounds = [...new Set(campaignRecipients.map(r => r.round_number || 0))].sort((a, b) => a - b);
                      return rounds.map(round => (
                        <option key={round} value={round.toString()}>
                          Round {round}
                        </option>
                      ));
                    })()}
                  </select>
                </div>
              </div>
              {selectedRecipients.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-blue-50 rounded-md">
                  <span className="text-sm font-medium text-blue-700">
                    {selectedRecipients.length} selected
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleBulkAction('pause')}
                      className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
                    >
                      Pause
                    </button>
                    <button
                      onClick={() => handleBulkAction('unpause')}
                      className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      Unpause
                    </button>
                    <button
                      onClick={() => handleBulkAction('cold_to_warm')}
                      className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                    >
                      Cold → Warm
                    </button>
                    <button
                      onClick={() => handleBulkAction('warm_to_active')}
                      className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      Warm → Active
                    </button>
                    <button
                      onClick={() => setSelectedRecipients([])}
                      className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 ml-2"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>

            {loadingDetails ? (
              <div className="p-8 text-center text-gray-500">Loading recipients...</div>
            ) : campaignRecipients.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No recipients found</div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {showFullUI && (<th className="text-left p-3 font-medium text-gray-900 w-12">
                        <input
                          type="checkbox"
                          checked={selectedRecipients.length === filteredAndSortedRecipients.length && filteredAndSortedRecipients.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>)}
                      <th className="text-left p-3 font-medium text-gray-500 w-12">#</th>
                      <th className="text-left p-3 font-medium text-gray-900">Full Name</th>
                      <th className="text-left p-3 font-medium text-gray-900">Email</th>
                      <th className="text-left p-3 font-medium text-gray-900">Company</th>
                      <th className="text-left p-3 font-medium text-gray-900">Location</th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center gap-1">
                          Status
                          {sortColumn === 'status' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      {/*<th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('engagement')}
                      >
                        <div className="flex items-center gap-1">
                          Engagement
                          {sortColumn === 'engagement' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>*/}
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('leadType')}
                      >
                        <div className="flex items-center gap-1">
                          Type
                          {sortColumn === 'leadType' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('opens')}
                      >
                        <div className="flex items-center gap-1">
                          Opens
                          {sortColumn === 'opens' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('clicks')}
                      >
                        <div className="flex items-center gap-1">
                          Clicks
                          {sortColumn === 'clicks' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('round')}
                      >
                        <div className="flex items-center gap-1">
                          Round
                          {sortColumn === 'round' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      <th
                        className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('schedule')}
                      >
                        <div className="flex items-center gap-1">
                          Schedule
                          {sortColumn === 'schedule' && (
                            sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                          )}
                        </div>
                      </th>
                      {showFullUI && (
                        <th
                          className="text-left p-3 font-medium text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
                          onClick={() => handleSort('pause')}
                        >
                          <div className="flex items-center gap-1">
                            Pause
                            {sortColumn === 'pause' && (
                              sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                            )}
                          </div>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredAndSortedRecipients.map((recipient, index) => (
                      <tr key={recipient.id} className="hover:bg-gray-50">
                        {showFullUI && (<td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedRecipients.includes(recipient.id)}
                            onChange={() => handleSelectRecipient(recipient.id)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        )}
                        <td className="p-3 text-gray-500 font-mono w-12">{index + 1}</td>
                        <td className="p-3">
                          <div
                            className="max-w-[180px] whitespace-nowrap truncate"
                            title={`${recipient.first_name || ''} ${recipient.last_name || ''}`.trim()}
                          >
                            {recipient.first_name} {recipient.last_name}
                          </div>
                        </td>
                        <td className="p-3 text-blue-600">
                          {recipient.email}
                        </td>
                        <td className="p-3">
                          {recipient.company || 'N/A'}
                        </td>
                        <td className="p-3">
                          {recipient.location || recipient.country ?
                            `${recipient.location || ''}, ${recipient.country || ''}`.replace(/^, |, $/, '') :
                            'N/A'}
                        </td>
                        <td className="p-3">
                          <span className={`inline-block px-2 py-1 text-xs rounded-full ${getRecipientStatusColor(recipient.status)}`}>
                            {(recipient.status || 'pending').charAt(0).toUpperCase() + (recipient.status || 'pending').slice(1)}
                          </span>
                        </td>
                        {/*<td className="p-3">
                          {(() => {
                            const engagementInfo = getEngagementInfo(recipient);
                            const IconComponent = engagementInfo.tierInfo.icon;
                            return (
                              <div className="flex items-center gap-2">
                                <div className={`p-1 rounded-full ${engagementInfo.tierInfo.bgColor}`}
                                  title={`${engagementInfo.tierInfo.title} - Campaign Score: ${engagementInfo.campaignScore} | Opens: ${engagementInfo.openCount}, Clicks: ${engagementInfo.clickCount}, Replies: ${engagementInfo.responseCount}`}>
                                  <IconComponent size={16} className={engagementInfo.tierInfo.color} />
                                </div>
                                <div className="flex flex-col text-xs">
                                  <span className="text-gray-700 font-medium">{engagementInfo.campaignScore}</span>
                                  <span className="text-gray-400">
                                    {engagementInfo.openCount}📧 {engagementInfo.clickCount}🔗 {engagementInfo.responseCount}💬
                                  </span>
                                </div>
                              </div>
                            );
                          })()}
                        </td>*/}
                        <td className="p-3">
                          {(() => {
                            const recipientType = recipient.recipient_type || 'cold_leads';
                            const typeDisplay = {
                              cold_leads: { text: 'Cold', color: 'bg-blue-100 text-blue-800' },
                              warm_prospects: { text: 'Warm', color: 'bg-yellow-100 text-yellow-800' },
                              active_clients: { text: 'Active', color: 'bg-green-100 text-green-800' }
                            };
                            const display = typeDisplay[recipientType] || typeDisplay.cold_leads;
                            return (
                              <span className={`inline-block px-2 py-1 text-xs rounded-full font-medium ${display.color}`}>
                                {display.text}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-block px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm font-medium">
                            {recipient.open_count || 0}
                          </span>
                        </td>
                        <td className="p-3 text-center">
                          <span className="inline-block px-2 py-1 bg-purple-50 text-purple-700 rounded text-sm font-medium">
                            {recipient.click_count || 0}
                          </span>
                        </td>

                        <td className="p-3">
                          {recipient.round_number || 0}
                        </td>
                        <td className="p-3">
                          {recipient.next_scheduled_at ? templateUtils.utcToLocalInput(recipient.next_scheduled_at, false) :
                            (selectedCampaign.scheduled_at ? templateUtils.utcToLocalInput(selectedCampaign.scheduled_at, false) : "")}
                        </td>
                        {showFullUI && (
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {recipient.recipient_status === 'completed' && <span className={`inline-block px-2 py-1 text-xs rounded-full ${getRecipientStatusColor2(recipient.recipient_status)}`}>
                                {(recipient.recipient_status || 'active').charAt(0).toUpperCase() + (recipient.recipient_status || 'active').slice(1)}
                              </span>
                              }
                              {recipient.recipient_status !== 'completed' && (
                                <input
                                  type="checkbox"
                                  checked={recipient.recipient_status === 'paused' || false}
                                  onChange={() => toggleRecipientStatus(recipient.id, recipient.recipient_status)}
                                  disabled={loading}
                                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                />
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => {
                // Export to CSV functionality
                const csvData = filteredAndSortedRecipients.map(recipient => ({
                  firstName: recipient.first_name || '',
                  lastName: recipient.last_name || '',
                  email: recipient.email,
                  company: recipient.company || '',
                  location: recipient.location || '',
                  country: recipient.country || '',
                  status: recipient.status || 'pending',
                  openCount: recipient.open_count || 0,
                  clickCount: recipient.click_count || 0
                }));

                const csvContent = [
                  ['First Name', 'Last Name', 'Email', 'Company', 'Location', 'Country', 'Status', 'Open Count', 'Click Count'],
                  ...csvData.map(row => [row.firstName, row.lastName, row.email, row.company, row.location, row.country, row.status, row.openCount, row.clickCount])
                ].map(row => row.map(field => `"${field}"`).join(',')).join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${selectedCampaign.name}-recipients.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Export to CSV
            </button>
            <button
              onClick={() => {
                const campaignId = selectedCampaign?.id;
                setShowDetailsModal(false);
                setSelectedCampaign(null);
                setCampaignRecipients([]);
                scrollToCampaign(campaignId);
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Close
            </button>
          </div>
        </div>

      </div>
    );
  }

  console.log('EmailCampaigns render: showContactPanel=', showContactPanel, 'selectedCampaign=', selectedCampaign);
  console.log('EmailCampaigns render: about to check if showContactPanel is true...');

  const totalPages = Math.ceil(totalCampaigns / PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Campaigns</h2>
          <p className="text-gray-600">Manage and track email campaigns</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus size={16} />
          New Campaign
        </button>
      </div>

      {/* Two-column layout: Folders sidebar + Campaigns */}
      <div className="flex gap-6">
        {/* Folders Sidebar */}
        <FolderManager
          items={campaigns}
          itemFolderIdField="folder_id"
          itemCounts={folderCounts}
          selectedFolderId={selectedFolderId}
          onFolderSelect={(folderId) => {
            setSelectedFolderId(folderId);
            setCurrentPage(1);
            loadCampaigns(1, folderId);
          }}
          allItemsLabel="All Campaigns"
          folderType="campaign"
          onFolderStatusChange={handleFolderCampaignStatus}
        />

        {/* Campaigns Content */}
        <div className="flex-1">

          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder={selectedFolderId ? 'Search campaign cards below...' : 'Search campaigns by name...'}
              value={campaignSearch}
              onChange={e => {
                const val = e.target.value;
                setCampaignSearch(val);
                loadCampaigns(1, selectedFolderId, val);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Folder Summary — all folders when on root view, single folder when inside a folder */}
          {selectedFolderId && folders.find(folder => folder.id === selectedFolderId) && (
            <FolderPerformancePanel
              key={selectedFolderId}
              folder={folders.find(folder => folder.id === selectedFolderId)}
              onCampaignOpen={handleOpenFolderCampaign}
              onCampaignStatusChange={() => loadCampaigns(currentPage, selectedFolderId, campaignSearch)}
              canResume={Boolean(gmailStatus?.hasAccounts)}
            />
          )}

          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              {loading && campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">Loading campaigns...</div>
                </div>
              ) : campaigns.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    {selectedFolderId
                      ? `No campaigns found in this folder.`
                      : 'No email campaigns found. Create your first campaign!'
                    }
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {campaigns.map(campaign => (
                    <div key={campaign.id} id={`campaign-${campaign.id}`} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">{campaign.name}</h3>
                          <div className="flex gap-2 mt-1">
                            {/*<span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              {getTargetTypeLabel(campaign.target_type)}
                            </span>
                            */}
                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${getStatusColor(campaign.status)}`}>
                              {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                            </span>
                            {campaign.status === 'paused' && campaign.pause_reason && (
                              <span className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                {campaign.pause_reason.split("|")[0]}
                              </span>
                            )}
                            {campaign.folder_id && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                <Folder size={12} />
                                {folders.find(f => f.id === campaign.folder_id)?.name || 'Unknown'}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              handleViewCampaignDetails(campaign, null, true);
                            }}
                            className="p-2 text-gray-400 hover:text-green-600"
                            title="View Details"
                          >
                            <Eye size={16} />
                          </button>
                          {(campaign.status !== 'sending') && (<button
                            onClick={() => {
                              handleEditCampaign(campaign);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title="Edit Campaign"
                          >
                            <Edit3 size={16} />
                          </button>
                          )}

                          {/* Folder dropdown */}
                          <div className="relative folder-dropdown-container">
                            <button
                              onClick={() => setShowFolderDropdown(showFolderDropdown === campaign.id ? null : campaign.id)}
                              className="p-2 text-gray-400 hover:text-blue-600"
                              title="Move to folder"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                            {showFolderDropdown === campaign.id && (
                              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                                <div className="py-1">
                                  <div className="px-3 py-2 text-xs font-medium text-gray-500 border-b">
                                    Move to folder
                                  </div>
                                  <button
                                    onClick={() => {
                                      handleMoveCampaignToFolder(campaign.id, null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                  >
                                    <div className="flex items-center gap-2">
                                      <Folder size={14} />
                                      No folder
                                    </div>
                                  </button>
                                  {folders.map(folder => (
                                    <button
                                      key={folder.id}
                                      onClick={() => {
                                        handleMoveCampaignToFolder(campaign.id, folder.id);
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${campaign.folder_id === folder.id ? 'bg-blue-50 text-blue-700' : ''
                                        }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        <Folder size={14} />
                                        {folder.name}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          {(campaign.status === 'scheduled' || campaign.status === 'sent' || campaign.status === 'sending') && (
                            <button
                              onClick={() => handlePauseCampaign(campaign.id)}
                              className="p-2 text-gray-400 hover:text-orange-600"
                              disabled={loading}
                              title="Pause Campaign"
                            >
                              <Clock size={16} />
                            </button>
                          )}
                          {campaign.status === 'paused' && (
                            <button
                              onClick={() => handleResumeCampaign(campaign.id)}
                              className="p-2 text-gray-400 hover:text-green-600"
                              disabled={loading}
                              title="Resume Campaign"
                            >
                              <RefreshCw size={16} />
                            </button>
                          )}
                          {(campaign.status === 'cancelled' || campaign.status === 'paused' || campaign.status === 'completed' || campaign.status === 'draft') && (<button
                            onClick={() => handleDeleteCampaign(campaign.id)}
                            className="p-2 text-gray-400 hover:text-red-600"
                            disabled={loading}
                            title="Delete Campaign"
                          >
                            <Trash2 size={16} />
                          </button>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {/* Current Step Information */}
                        <div>
                          <p className="text-sm font-medium text-gray-700">Current Step:</p>
                          <p className="text-sm text-gray-600">
                            {Math.min(campaign.templates?.length || 1, campaign.current_round + 1 || 1)} of {campaign.templates?.length || 1}
                            {campaign.templates?.length > 1 && (
                              <span className="ml-2 text-xs text-blue-600">
                                ({campaign.current_round === 0 ? 'Initial Email' : `Follow-up ${(campaign.current_round || 0)}`})
                              </span>
                            )}
                          </p>
                        </div>

                        {(() => {
                          // Get current step template using sequence_order matching current_round
                          const currentRound = campaign.current_round || 0;
                          const currentTemplate = campaign.templates?.find(t => t.sequence_order === currentRound - 1);

                          // If we have current template, get template details from templates table
                          if (currentTemplate?.template_id && templates.length > 0) {
                            const templateDetails = templates.find(t => t.id === currentTemplate.template_id);
                            if (templateDetails) {
                              return (
                                <>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Subject:</p>
                                    <p className="text-sm text-gray-600">{templateDetails.subject}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Preview:</p>
                                    <div className="text-sm text-gray-600 line-clamp-2">
                                      {htmlToPlainText(templateDetails.body)}
                                    </div>
                                  </div>
                                </>
                              );
                            }
                          }

                          // Fallback: try to get initial template (sequence_order = 0) if current round template not found
                          const fallbackTemplate = campaign.templates?.find(t => t.sequence_order === 0);
                          if (fallbackTemplate?.template_id && templates.length > 0) {
                            const templateDetails = templates.find(t => t.id === fallbackTemplate.template_id);
                            if (templateDetails) {
                              return (
                                <>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Subject:</p>
                                    <p className="text-sm text-gray-600">{templateDetails.subject}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-700">Preview:</p>
                                    <div className="text-sm text-gray-600 line-clamp-2">
                                      {htmlToPlainText(templateDetails.body)}
                                    </div>
                                  </div>
                                </>
                              );
                            }
                          }

                          // Final fallback to old campaign fields if templates not available
                          return (
                            <>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Subject:</p>
                                <p className="text-sm text-gray-600">{campaign.subject || 'No subject available'}</p>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">Preview:</p>
                                <p className="text-sm text-gray-600 line-clamp-2">{htmlToPlainText(campaign.body) || 'No preview available'}</p>
                              </div>
                            </>
                          );
                        })()}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-2">Overall Performance</div>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
                          <div className="text-center p-1.5 rounded border border-transparent hover:border-gray-200">
                            <div className="text-base font-semibold text-gray-900">{campaign.total_recipients}</div>
                            <div className="text-[11px] text-gray-500">Recipients</div>
                          </div>
                          <div
                            className="text-center cursor-pointer hover:bg-gray-50 p-1.5 rounded border border-transparent hover:border-blue-100 transition-colors"
                            onClick={() => handleViewCampaignDetails(campaign, 'sent', false)}
                          >
                            <div className="text-base font-semibold text-blue-600">{campaign.sent_count}</div>
                            <div className="text-[11px] text-gray-500">Sent</div>
                            <div className="text-[10px] text-gray-400">
                              ({campaign.remain_sent_count === 0 ? (campaign.templates?.length > campaign.current_round ? campaign.total_recipients : 0) : campaign.remain_sent_count} left)
                            </div>
                          </div>
                          <div
                            className="text-center cursor-pointer hover:bg-gray-50 p-1.5 rounded border border-transparent hover:border-teal-100 transition-colors"
                            onClick={() => handleViewCampaignDetails(campaign, 'bounced', false)}
                          >
                            <div className="text-base font-semibold text-teal-600">{campaign.bounce_count || 0}</div>
                            <div className="text-[11px] text-teal-500">
                              {campaign.sent_count > 0 ? Math.round(((campaign.bounce_count || 0) / campaign.sent_count) * 100) + '%' : '0%'}
                            </div>
                            <div className="text-[11px] text-gray-500">Bounce</div>
                          </div>
                          <div
                            className="text-center cursor-pointer hover:bg-gray-50 p-1.5 rounded border border-transparent hover:border-teal-100 transition-colors"
                            onClick={() => handleViewCampaignDetails(campaign, 'open', false)}
                          >
                            <div className="text-base font-semibold text-teal-600">{campaign.unique_open || 0}</div>
                            <div className="text-[11px] text-teal-500">
                              {campaign.sent_count > 0 ? Math.round(((campaign.unique_open || 0) / campaign.sent_count) * 100) + '%' : '0%'}
                            </div>
                            <div className="text-[11px] text-gray-500">Opens</div>
                          </div>
                          <div
                            className="text-center cursor-pointer hover:bg-gray-50 p-1.5 rounded border border-transparent hover:border-indigo-100 transition-colors"
                            onClick={() => handleViewCampaignDetails(campaign, 'click', false)}
                          >
                            <div className="text-base font-semibold text-indigo-600">{campaign.unique_click || 0}</div>
                            <div className="text-[11px] text-indigo-500">
                              {(campaign.sent_count - (campaign.bounce_count || 0)) > 0
                                ? Math.round(((campaign.unique_click || 0) / (campaign.sent_count - (campaign.bounce_count || 0))) * 100) + '%'
                                : '0%'}
                            </div>
                            <div className="text-[11px] text-gray-500">Clicks</div>
                          </div>
                          <div
                            className="text-center cursor-pointer hover:bg-gray-50 p-1.5 rounded border border-transparent hover:border-purple-100 transition-colors"
                            onClick={() => handleViewCampaignDetails(campaign, 'replied', false)}
                          >
                            <div className="text-base font-semibold text-purple-600">{campaign.replied_count}</div>
                            <div className="text-[11px] text-purple-500">
                              {campaign.sent_count > 0 ? Math.round(((campaign.replied_count || 0) / campaign.sent_count) * 100) + '%' : '0%'}
                            </div>
                            <div className="text-[11px] text-gray-500">Replied</div>
                          </div>
                        </div>
                      </div>

                      {campaign.templates?.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-dashed border-gray-200">
                          <div className="flex items-center justify-between text-sm font-semibold text-gray-700">
                            <span>{campaign.templates.length > 1 ? 'Sequence Performance' : 'Step Performance'}</span>
                            {campaign.templates.length > 1 && (
                              <span className="text-xs text-blue-600 font-semibold">
                                {campaign.templates.length} steps
                              </span>
                            )}
                          </div>
                          <div className="mt-3 space-y-3">
                            {campaign.templates
                              .slice()
                              .sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0))
                              .map((template, index) => {
                                const metrics = template.metrics || {};
                                const stepNumber = (template.sequence_order ?? index) + 1;
                                const stepLabel = stepNumber === 1 ? 'Initial email' : `Follow-up ${stepNumber}`;

                                return (
                                  <div
                                    key={`${campaign.id}-${template.sequence_order ?? index}-${template.template_id || 'template'}`}
                                    className="p-3 bg-gray-50 border border-gray-100 rounded-lg"
                                  >
                                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                                      <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                                        {stepLabel}
                                      </span>
                                      <span className="text-gray-700 truncate flex-1 min-w-0">
                                        {template.template_name || template.subject || 'Untitled Template'}
                                      </span>
                                      <span className="text-[11px] font-semibold text-gray-500 whitespace-nowrap">
                                        {formatMetricCount(metrics.sent_count ?? metrics.total_emails ?? 0)} sent
                                      </span>
                                    </div>
                                    <div className="mt-2 flex items-center gap-4 overflow-x-auto whitespace-nowrap pr-2">
                                      <StepMetric label="Delivered" value={formatMetricCount(metrics.delivered_count ?? metrics.sent_count ?? 0)} />
                                      <StepMetric label="Replies" value={`${formatMetricCount(metrics.replied_count)} / ${formatMetricRate(metrics.response_rate)}`} />
                                      <StepMetric label="Opens" value={`${formatMetricCount(metrics.unique_opens)} / ${formatMetricRate(metrics.open_rate)}`} emphasize />
                                      <StepMetric label="Clicks" value={`${formatMetricCount(metrics.unique_clicks ?? 0)} / ${formatMetricRate(metrics.click_rate)}`} emphasize />
                                      {stepNumber > 1 && (
                                        <StepMetric
                                          label="Scheduled"
                                          value={template.scheduled_send_at
                                            ? templateUtils.utcToLocaleDate(template.scheduled_send_at)
                                            : 'Not scheduled'}
                                        />
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-100">
                        <div className="flex gap-4 text-xs text-gray-500">
                          <span>Created: {templateUtils.utcToLocaleDate(campaign.created_at)}</span>
                          {campaign.scheduled_at && (
                            <span>Scheduled: {templateUtils.utcToLocaleDate(campaign.scheduled_at, false)}</span>
                          )}
                        </div>
                        {/* Display scheduling configuration */}
                        {(campaign.days || campaign.start_time || campaign.end_time || campaign.time_zone) && (
                          <div className="text-xs text-blue-700">
                            <strong>Active:</strong> {
                              campaign.days ?
                                JSON.parse(campaign.days).map(day => day.substring(0, 2)).join(', ') :
                                'Mo, Tu, We, Th, Fr'
                            } • {
                              campaign.start_time ?
                                campaign.start_time :
                                '06:00'
                            } AM to {
                              campaign.end_time ?
                                campaign.end_time :
                                '18:00'
                            } PM ({
                              campaign.time_zone ||
                              'America/New_York'
                            })
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => loadCampaigns(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <div className="flex gap-1 items-center">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => loadCampaigns(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg ${pageNum === currentPage ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => loadCampaigns(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

export const ContactManagement = ({ producers, producerColors, gmailStatus }) => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    producer: getDefaultProducerFilter(user, producers),
    clientType: 'All Client Types',
    source: 'All Sources',
    name: '',
    company: '',
    country: '',
    jobTitle: '',
    tag: 'All Tags',
    searchInNotes: false,
    showDuplicates: false
  });
  const [appliedContactFieldFilters, setAppliedContactFieldFilters] = useState({
    company: '',
    country: '',
    jobTitle: ''
  });
  const [stats, setStats] = useState({
    coldLeads: 0,
    warmProspects: 0,
    activeClients: 0,
    total: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 15
  });
  const [showContactPanel, setShowContactPanel] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const [editingContactInfo, setEditingContactInfo] = useState({});
  const [contactInfoValues, setContactInfoValues] = useState({});
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [notesModal, setNotesModal] = useState({
    isOpen: false,
    contactId: null,
    contactType: null,
    currentNotes: '',
    openNewNoteForm: false
  });
  const [mergeModal, setMergeModal] = useState({
    isOpen: false,
    contact1: null,
    contact2: null,
    mergedContact: {}
  });
  const [bulkTagModal, setBulkTagModal] = useState({
    isOpen: false,
    action: 'add',
    selectedTags: [],
    newTags: ''
  });
  const [contactProfileModal, setContactProfileModal] = useState({
    isOpen: false,
    contact: null,
    loading: false,
    data: null,
    error: null
  });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [activeCompanyInput, setActiveCompanyInput] = useState(null);
  const companySearchTimeoutRef = useRef(null);
  const profileCompanyCallbackRef = useRef(null);
  const [companyDetailsModal, setCompanyDetailsModal] = useState(() => createCompanyDetailsModalState());
  const [companyModal, setCompanyModal] = useState({
    isOpen: false,
    contactKey: null,
    submitting: false,
    values: {
      name: '',
      domain: '',
      website: '',
      phone: '',
      email: '',
      location: '',
      country: '',
      state: '',
      zip_code: '',
      address: ''
    }
  });

  const fetchCompanyOptions = useCallback(async (searchValue = '') => {
    try {
      setCompanySearchLoading(true);
      const params = new URLSearchParams();
      if (searchValue && searchValue.trim()) {
        params.append('search', searchValue.trim());
      }
      params.append('limit', '25');
      const response = await axios.get(`/api/contact-management/companies?${params.toString()}`);
      if (response.data.success) {
        setCompanyOptions(response.data.companies || []);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setCompanySearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (companySearchTimeoutRef.current) {
      clearTimeout(companySearchTimeoutRef.current);
      companySearchTimeoutRef.current = null;
    }

    const trimmedTerm = (companySearchTerm || '').trim();
    if (!trimmedTerm) {
      fetchCompanyOptions('');
    } else {
      companySearchTimeoutRef.current = setTimeout(() => {
        fetchCompanyOptions(trimmedTerm);
        companySearchTimeoutRef.current = null;
      }, 300);
    }

    return () => {
      if (companySearchTimeoutRef.current) {
        clearTimeout(companySearchTimeoutRef.current);
        companySearchTimeoutRef.current = null;
      }
    };
  }, [companySearchTerm, fetchCompanyOptions]);


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const nextFilters = {
        company: filters.company.trim(),
        country: filters.country.trim(),
        jobTitle: filters.jobTitle.trim()
      };
      setAppliedContactFieldFilters(previous => (
        previous.company === nextFilters.company
        && previous.country === nextFilters.country
        && previous.jobTitle === nextFilters.jobTitle
          ? previous
          : nextFilters
      ));
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [filters.company, filters.country, filters.jobTitle]);


  const loadContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append('page', currentPage.toString());
      params.append('limit', '15');

      // Add sorting parameters
      if (sortConfig.key) {
        params.append('sortBy', sortConfig.key);
        params.append('sortOrder', sortConfig.direction);
      }

      params.append('producer', filters.producer);
      if (filters.clientType !== 'All Client Types') {
        params.append('clientType', filters.clientType);
      }
      if (filters.source !== 'All Sources') {
        params.append('source', filters.source);
      }
      if (filters.name && filters.name.trim()) {
        params.append('name', filters.name.trim());
      }
      if (appliedContactFieldFilters.company) {
        params.append('company', appliedContactFieldFilters.company);
      }
      if (appliedContactFieldFilters.country) {
        params.append('country', appliedContactFieldFilters.country);
      }
      if (appliedContactFieldFilters.jobTitle) {
        params.append('jobTitle', appliedContactFieldFilters.jobTitle);
      }
      if (filters.tag !== 'All Tags') {
        params.append('tag', filters.tag);
      }
      if (filters.searchInNotes) {
        params.append('searchInNotes', filters.searchInNotes.toString());
      }
      if (filters.showDuplicates) {
        params.append('showDuplicates', filters.showDuplicates.toString());
      }

      const response = await axios.get(`/api/contact-management/contacts?${params.toString()}`);
      if (response.data.success) {
        setContacts(response.data.contacts);
        // Update pagination if returned by API
        if (response.data.pagination) {
          setPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Failed to load contacts:', error);
      alert('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [
    appliedContactFieldFilters,
    currentPage,
    filters.clientType,
    filters.name,
    filters.producer,
    filters.searchInNotes,
    filters.showDuplicates,
    filters.source,
    filters.tag,
    sortConfig
  ]);

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/contact-management/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadTemplates = async () => {
    try {
      const templates = await templateUtils.loadTemplates();
      setTemplates(templates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadTags = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.producer) {
        params.append('producer', filters.producer);
      }

      const response = await axios.get(`/api/contact-management/tags?${params.toString()}`);
      if (response.data.success) {
        setAvailableTags(response.data.tags);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  }, [filters.producer]);

  useEffect(() => {
    loadContacts();
    loadStats();
    loadTemplates();
  }, [loadContacts, loadStats]);

  // Separate useEffect for loading tags when producer changes
  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // Use contacts directly - duplicate filtering should be done server-side
  const filteredContacts = contacts;

  const handleFilterChange = (filterType, value) => {

    // Reset to first page when toggling duplicates
    if (filterType === 'showDuplicates') {
      setCurrentPage(1);
    }

    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterType]: value
      };

      // Reset tag filter when producer changes since available tags will change
      if (filterType === 'producer') {
        newFilters.tag = 'All Tags';
      }

      return newFilters;
    });
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters(previous => ({
      ...previous,
      clientType: 'All Client Types',
      source: 'All Sources',
      name: '',
      company: '',
      country: '',
      jobTitle: '',
      tag: 'All Tags',
      searchInNotes: false,
      showDuplicates: false
    }));
    setAppliedContactFieldFilters({ company: '', country: '', jobTitle: '' });
    setCurrentPage(1);
  };



  const handleDeleteContact = async (contactId, contactType, source, zohoId) => {
    // Special alert for Zoho contacts
    if (source === 'zoho' && zohoId) {
      if (!window.confirm('⚠️ This contact will be deleted from both the system AND Zoho CRM. This action cannot be undone. Are you sure you want to proceed?')) {
        return;
      }
    } else {
      if (!window.confirm('Are you sure you want to delete this contact?')) return;
    }

    try {
      setLoading(true);

      // Build query parameters
      const params = new URLSearchParams({
        contactType: contactType
      });

      // Add source and zoho_id if it's a Zoho contact
      if (source) {
        params.append('source', source);
      }
      if (zohoId) {
        params.append('zoho_id', zohoId);
      }

      const response = await axios.delete(`/api/contact-management/contacts/${contactId}?${params.toString()}`);

      if (response.data.success) {
        alert('Contact deleted successfully!');
        loadContacts();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
      alert('Failed to delete contact: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenContactProfile = async (contact) => {
    if (!contact?.id) return;

    setContactProfileModal({
      isOpen: true,
      contact,
      loading: true,
      data: null,
      error: null
    });

    try {
      const response = await axios.get(`/api/contact-management/contacts/${contact.id}/profile`);
      if (response.data.success) {
        setContactProfileModal(prev => ({
          ...prev,
          loading: false,
          data: response.data.profile
        }));
      } else {
        throw new Error('Failed to load contact profile');
      }
    } catch (error) {
      console.error('Failed to load contact profile:', error);
      setContactProfileModal(prev => ({
        ...prev,
        loading: false,
        error: 'Unable to load contact profile'
      }));
    }
  };

  const handleCloseContactProfile = () => {
    setContactProfileModal({
      isOpen: false,
      contact: null,
      loading: false,
      data: null,
      error: null
    });
  };

  const handleContactProfileUpdate = (updatedContact) => {
    if (!updatedContact?.id) return;

    const updatedForList = Array.isArray(updatedContact.tags)
      ? { ...updatedContact, tags: JSON.stringify(updatedContact.tags) }
      : updatedContact;

    setContacts(prev =>
      prev.map(contact =>
        contact.id === updatedForList.id
          ? { ...contact, ...updatedForList }
          : contact
      )
    );

    setContactProfileModal(prev => {
      const nextContact = prev.contact?.id === updatedContact.id
        ? { ...prev.contact, ...updatedContact }
        : prev.contact;

      const nextData = prev.data?.contact?.id === updatedContact.id
        ? {
            ...prev.data,
            contact: { ...prev.data.contact, ...updatedContact }
          }
        : prev.data;

      return {
        ...prev,
        contact: nextContact,
        data: nextData
      };
    });
  };

  const handleDeleteTagGlobally = async (tagToDelete) => {

    if (!window.confirm(`Are you sure you want to delete the "${tagToDelete}" tag from ALL contacts? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);

      // Use the new API endpoint to delete the tag globally
      const response = await axios.delete(`/api/contact-management/tags/${encodeURIComponent(tagToDelete)}`, {
        params: {
          producer: filters.producer
        }
      });

      if (response.data.success) {
        // If the deleted tag was currently selected, reset to "All Tags"
        if (filters.tag === tagToDelete) {
          setFilters(prev => ({ ...prev, tag: 'All Tags' }));
        }

        // Refresh data
        loadContacts();
        loadStats();
        loadTags(); // Refresh available tags list

        alert(response.data.message);
      }
    } catch (error) {
      console.error('Failed to delete tag globally:', error);
      alert('Failed to delete tag: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNotesModal = (contactId, contactType, currentNotes, openNewNoteForm = false) => {
    setNotesModal({
      isOpen: true,
      contactId,
      contactType,
      currentNotes: currentNotes || '',
      openNewNoteForm
    });
  };

  const handleCloseNotesModal = () => {
    setNotesModal({
      isOpen: false,
      contactId: null,
      contactType: null,
      currentNotes: ''
    });
  };

  const handleSaveNotes = (contactId, contactType, newNotes) => {
    const hasContent = Boolean(newNotes && newNotes.trim().length);
    const safeNotes = newNotes ?? '';

    // Update the local contacts state so the latest note text re-renders in the table right away
    setContacts(prev => prev.map(contact =>
      contact.id === contactId && contact.contact_type === contactType
        ? {
          ...contact,
          notes: safeNotes,
          latest_note: hasContent ? safeNotes : null
        }
        : contact
    ));

    // Keep modal state in sync so reopening reflects the saved content
    setNotesModal(prev => ({
      ...prev,
      currentNotes: safeNotes
    }));
  };

  const handleOpenMergeModal = () => {
    if (selectedContacts.size !== 2) {
      alert('Please select exactly 2 contacts to merge.');
      return;
    }

    const contactsArray = Array.from(selectedContacts.values());
    const contact1 = contactsArray[0];
    const contact2 = contactsArray[1];

    // Initialize merged contact with contact1 as default, user can change
    setMergeModal({
      isOpen: true,
      contact1,
      contact2,
      mergedContact: {
        first_name: contact1.first_name,
        last_name: contact1.last_name,
        email: contact1.email,
        phone: contact1.phone,
        job_title: contact1.job_title,
        company: contact1.company,
        location: contact1.location,
        country: contact1.country,
        tags: contact1.tags,
        notes: contact1.notes,
        //contact_type: contact1.contact_type,
        //source: contact1.source,
        //producer_name: contact1.producer_name
      }
    });
  };

  const handleCloseMergeModal = () => {
    setMergeModal({
      isOpen: false,
      contact1: null,
      contact2: null,
      mergedContact: {}
    });
  };

  const handleMergeContacts = async () => {
    try {
      setLoading(true);

      const { contact1, contact2, mergedContact } = mergeModal;

      // Use the single merge endpoint
      const response = await axios.post('/api/contact-management/contacts/merge', {
        primaryContactId: contact1.id,
        primaryContactType: contact1.contact_type,
        secondaryContactId: contact2.id,
        secondaryContactType: contact2.contact_type,
        mergedData: {
          first_name: mergedContact.first_name,
          last_name: mergedContact.last_name,
          email: mergedContact.email,
          phone: mergedContact.phone,
          job_title: mergedContact.job_title,
          company: mergedContact.company,
          location: mergedContact.location,
          country: mergedContact.country,
          tags: mergedContact.tags,
          notes: mergedContact.notes,
          //producer_name: mergedContact.producer_name
        }
      });

      if (response.data.success) {
        // Refresh data
        loadContacts();
        loadStats();
        handleClearSelection();
        handleCloseMergeModal();

        alert('Contacts merged successfully!');
      }
    } catch (error) {
      console.error('Failed to merge contacts:', error);
      alert('Failed to merge contacts: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllSelected = async () => {
    if (selectedContacts.size === 0) {
      alert('No contacts selected for deletion.');
      return;
    }

    const selectedContactsList = Array.from(selectedContacts.values());
    const zohoContacts = selectedContactsList.filter(contact => contact.source === 'zoho' && contact.zoho_id);
    const regularContacts = selectedContactsList.filter(contact => !(contact.source === 'zoho' && contact.zoho_id));

    let confirmMessage = `Are you sure you want to delete ${selectedContacts.size} selected contact${selectedContacts.size !== 1 ? 's' : ''}?`;

    if (zohoContacts.length > 0) {
      confirmMessage = `⚠️ WARNING: ${zohoContacts.length} of the selected contacts are from Zoho CRM and will be deleted from both the system AND Zoho CRM.\n\n${confirmMessage}\n\nThis action cannot be undone.`;
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      // Process deletions one by one to handle individual contact types and sources
      for (const contact of selectedContactsList) {
        try {
          // Extract contact type from the key (e.g., "cold-123" -> "cold")
          const key = Array.from(selectedContacts.keys()).find(k => selectedContacts.get(k) === contact);
          const contactType = key ? key.split('-')[0] : 'cold'; // fallback to 'cold'

          // Build query parameters
          const params = new URLSearchParams({
            contactType: contactType
          });

          // Add source and zoho_id if it's a Zoho contact
          if (contact.source) {
            params.append('source', contact.source);
          }
          if (contact.zoho_id) {
            params.append('zoho_id', contact.zoho_id);
          }

          const response = await axios.delete(`/api/contact-management/contacts/${contact.id}?${params.toString()}`);

          if (response.data.success) {
            successCount++;
          } else {
            failCount++;
            errors.push(`${contact.first_name} ${contact.last_name}: ${response.data.error || 'Unknown error'}`);
          }
        } catch (error) {
          failCount++;
          errors.push(`${contact.first_name} ${contact.last_name}: ${error.response?.data?.error || error.message}`);
        }
      }

      // Clear selection and refresh data
      setSelectedContacts(new Map());
      setSelectAll(false);
      await loadContacts();
      await loadStats();

      // Show results
      let resultMessage = `Deletion completed!\n✅ ${successCount} contact${successCount !== 1 ? 's' : ''} deleted successfully`;

      if (failCount > 0) {
        resultMessage += `\n❌ ${failCount} contact${failCount !== 1 ? 's' : ''} failed to delete`;
        if (errors.length > 0) {
          resultMessage += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
          if (errors.length > 5) {
            resultMessage += `\n... and ${errors.length - 5} more errors`;
          }
        }
      }

      alert(resultMessage);
    } catch (error) {
      console.error('Failed to delete selected contacts:', error);
      alert('Failed to delete selected contacts: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteContact = async (contactId, fromType, toType) => {
    const confirmMsg = `Move contact from ${fromType} to ${toType}?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      setLoading(true);
      const response = await axios.put('/api/contact-management/contacts/bulk-promote', {
        contactIds: [contactId],
        fromType,
        toType,
        notes: `Promoted from ${fromType} to ${toType} on ${new Date().toLocaleDateString()}`
      });

      if (response.data.success) {
        alert(`Contact moved to ${toType} successfully!`);
        loadContacts();
        loadStats();
      }
    } catch (error) {
      console.error('Failed to promote contact:', error);
      alert('Failed to move contact: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleStartContactInfoEdit = (contactId, contactType, contact) => {
    if (contact.user_id !== user?.id) {
      alert('You can only edit contacts that belong to you.');
      return;
    }

    const key = `${contactType}-${contactId}`;
    setEditingContactInfo(prev => ({ ...prev, [key]: true }));

    // Parse tags if they exist
    let parsedTags = [];
    if (contact.tags) {
      try {
        const tags = JSON.parse(contact.tags);
        if (Array.isArray(tags)) {
          parsedTags = tags;
        }
      } catch (e) {
        // If parsing fails, treat as empty array
        parsedTags = [];
      }
    }

    const combinedName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
    const initialCompanyId = contact.companies_id ?? contact.company_id ?? null;

    setContactInfoValues(prev => ({
      ...prev,
      [key]: {
        email: contact.email || '',
        tags: parsedTags,
        assignment: contact.producer_name || null,
        producer_name: contact.producer_name || '',
        fullName: combinedName,
        companyName: contact.company || '',
        companyId: initialCompanyId
      }
    }));
  };

  const handleCancelContactInfoEdit = (contactId, contactType) => {
    const key = `${contactType}-${contactId}`;
    setEditingContactInfo(prev => ({ ...prev, [key]: false }));
    setContactInfoValues(prev => ({ ...prev, [key]: {} }));
    setCompanySearchTerm('');
    setActiveCompanyInput(prev => (prev === key ? null : prev));
  };

  const handleUpdateContactInfo = async (contactId, contactType) => {
    console.log(`Updating contact info for ID: ${contactId}, Type: ${contactType}`);
    const contactToUpdate = contacts.find(contact =>
      contact.id === contactId && contact.contact_type === contactType
    );

    if (contactToUpdate && contactToUpdate.user_id !== user?.id) {
      alert('You can only edit contacts that belong to you.');
      return;
    }

    const key = `${contactType}-${contactId}`;
    const values = contactInfoValues[key];

    if (!values) return;

    try {
      setLoading(true);

      // Process tags - handle both array (new format) and string (old format)
      let tagsArray = [];
      if (values.tags) {
        if (Array.isArray(values.tags)) {
          tagsArray = values.tags.filter(tag => tag && tag.trim().length > 0);
        } else if (values.tags.trim()) {
          tagsArray = values.tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
        }
      }

      // Process name splitting if editing a name
      let firstName = '';
      let lastName = '';
      if (values.fullName && values.fullName.trim()) {
        const nameParts = values.fullName.trim().split(' ');
        firstName = nameParts[0] || '';
        lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
      }

      // Prepare update data
      console.log("values" + values.assignment);
      const updateData = {
        email: values.email,
        tags: JSON.stringify(tagsArray),
        producer_name: values.assignment && values.assignment !== 'Unassigned' ? values.assignment?.id : null
      };

      if (values.companyName !== undefined) {
        updateData.company_name = values.companyName;
      }

      if (values.companyId !== undefined) {
        updateData.company_id = values.companyId ?? null;
      }

      // Add name fields if editing a name (for email_forward contacts) and name was actually provided
      if (values.fullName !== undefined && values.fullName.trim()) {
        updateData.first_name = firstName;
        updateData.last_name = lastName;
      }

      // Update the contact information.
      const infoResponse = await axios.put(`/api/contact-management/contacts/${contactId}/info?contactType=${contactType}`, updateData);

      if (infoResponse.data.success) {
        const cleanedCompanyName = values.companyName !== undefined ? (values.companyName || '').trim() : undefined;

        // Update the local state to reflect the changes
        setContacts(prev => prev.map(contact =>
          contact.id === contactId && contact.contact_type === contactType
            ? {
              ...contact,
              email: values.email,
              tags: JSON.stringify(tagsArray),
              producer_name: values.assignment,
              ...(values.companyName !== undefined && {
                company: cleanedCompanyName || null
              }),
              ...(values.companyId !== undefined && {
                company_id: values.companyId ?? null,
                companies_id: values.companyId ?? null
              }),
              ...(values.fullName !== undefined && values.fullName.trim() && {
                first_name: firstName,
                last_name: lastName
              })
            }
            : contact
        ));

        // Close edit mode
        setEditingContactInfo(prev => ({ ...prev, [key]: false }));
        setContactInfoValues(prev => ({ ...prev, [key]: {} }));
        setCompanySearchTerm('');
        setActiveCompanyInput(null);

        alert('Contact updated successfully!');
      }
    } catch (error) {
      console.error('Failed to update contact info:', error);
      alert('Failed to update contact info. Please try again.' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyInputChange = (contactKey, value) => {
    setContactInfoValues(prev => ({
      ...prev,
      [contactKey]: {
        ...prev[contactKey],
        companyName: value,
        companyId: null
      }
    }));
    setCompanySearchTerm(value);
  };

  const handleCompanySuggestionSelect = (contactKey, company) => {
    setContactInfoValues(prev => ({
      ...prev,
      [contactKey]: {
        ...prev[contactKey],
        companyName: company.name,
        companyId: company.id
      }
    }));
    setCompanySearchTerm(company.name);
    setActiveCompanyInput(null);
  };

  const resetCompanyDetailsModal = () => setCompanyDetailsModal(createCompanyDetailsModalState());

  const handleViewCompanyDetails = async (companyId) => {
    if (!companyId) {
      alert('No linked company record for this contact.');
      return;
    }

    setCompanyDetailsModal({
      ...createCompanyDetailsModalState(),
      isOpen: true,
      companyId,
      loading: true
    });

    try {
      const response = await axios.get(`/api/contact-management/companies/${companyId}`);
      if (response.data.success && response.data.company) {
        const company = response.data.company;
        setCompanyDetailsModal(prev => ({
          ...prev,
          loading: false,
          data: company,
          values: {
            name: company.name || '',
            website: company.website || '',
            domain: company.domain || '',
            phone: company.phone || '',
            email: company.email || '',
            location: company.location || '',
            country: company.country || '',
            state: company.state || '',
            zip_code: company.zip_code || '',
            address: company.address || ''
          },
          error: null
        }));
      } else {
        setCompanyDetailsModal(prev => ({
          ...prev,
          loading: false,
          error: response.data.error || 'Failed to load company details'
        }));
      }
    } catch (error) {
      setCompanyDetailsModal(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.error || 'Failed to load company details'
      }));
    }
  };

  const handleCloseCompanyDetailsModal = () => {
    resetCompanyDetailsModal();
  };

  const handleCompanyDetailsFieldChange = (field, value) => {
    setCompanyDetailsModal(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value
      }
    }));
  };

  const handleStartCompanyDetailsEdit = () => {
    setCompanyDetailsModal(prev => ({
      ...prev,
      editing: true,
      error: null,
      values: {
        name: prev.data?.name || '',
        website: prev.data?.website || '',
        domain: prev.data?.domain || '',
        phone: prev.data?.phone || '',
        email: prev.data?.email || '',
        location: prev.data?.location || '',
        country: prev.data?.country || '',
        state: prev.data?.state || '',
        zip_code: prev.data?.zip_code || '',
        address: prev.data?.address || ''
      }
    }));
  };

  const handleCancelCompanyDetailsEdit = () => {
    setCompanyDetailsModal(prev => ({
      ...prev,
      editing: false,
      saving: false,
      error: null,
      values: {
        name: prev.data?.name || '',
        website: prev.data?.website || '',
        domain: prev.data?.domain || '',
        phone: prev.data?.phone || '',
        email: prev.data?.email || '',
        location: prev.data?.location || '',
        country: prev.data?.country || '',
        state: prev.data?.state || '',
        zip_code: prev.data?.zip_code || '',
        address: prev.data?.address || ''
      }
    }));
  };

  const handleSaveCompanyDetails = async () => {
    if (!companyDetailsModal.companyId) return;
    const trimmedName = (companyDetailsModal.values.name || '').trim();
    if (!trimmedName) {
      setCompanyDetailsModal(prev => ({
        ...prev,
        error: 'Company name is required.'
      }));
      return;
    }

    setCompanyDetailsModal(prev => ({
      ...prev,
      saving: true,
      error: null
    }));

    try {
      const response = await axios.put(`/api/contact-management/companies/${companyDetailsModal.companyId}`, {
        ...companyDetailsModal.values,
        name: trimmedName
      });

      if (response.data.success && response.data.company) {
        const updated = response.data.company;
        setCompanyDetailsModal(prev => ({
          ...prev,
          saving: false,
          editing: false,
          data: updated,
          values: {
            name: updated.name || '',
            website: updated.website || '',
            domain: updated.domain || '',
            phone: updated.phone || '',
            email: updated.email || '',
            location: updated.location || '',
            country: updated.country || '',
            state: updated.state || '',
            zip_code: updated.zip_code || '',
            address: updated.address || ''
          }
        }));

        setContacts(prev => prev.map(contact => {
          const contactCompanyId = contact.companies_id ?? contact.company_id;
          if (contactCompanyId === updated.id) {
            return {
              ...contact,
              company: updated.name,
              company_id: updated.id,
              companies_id: updated.id
            };
          }
          return contact;
        }));

        setCompanyOptions(prev => prev.map(option => option.id === updated.id ? { ...option, name: updated.name } : option));
        fetchCompanyOptions(companySearchTerm || '');
      } else {
        setCompanyDetailsModal(prev => ({
          ...prev,
          saving: false,
          error: response.data.error || 'Failed to update company.'
        }));
      }
    } catch (error) {
      setCompanyDetailsModal(prev => ({
        ...prev,
        saving: false,
        error: error.response?.data?.error || 'Failed to update company.'
      }));
    }
  };

  const handleCreateCompany = (contactKey) => {
    profileCompanyCallbackRef.current = null;
    setCompanyModal({
      isOpen: true,
      contactKey,
      submitting: false,
      values: {
        name: '',
        domain: '',
        website: '',
        phone: '',
        email: '',
        location: '',
        country: '',
        state: '',
        zip_code: '',
        address: ''
      }
    });
  };

  const handleCloseCompanyModal = () => {
    profileCompanyCallbackRef.current = null;
    setCompanyModal({
      isOpen: false,
      contactKey: null,
      submitting: false,
      values: {
        name: '',
        domain: '',
        website: '',
        phone: '',
        email: '',
        location: '',
        country: '',
        state: '',
        zip_code: '',
        address: ''
      }
    });
  };

  const handleCompanyModalFieldChange = (field, value) => {
    setCompanyModal(prev => ({
      ...prev,
      values: {
        ...prev.values,
        [field]: value
      }
    }));
  };

  const handleProfileRequestNewCompany = (_initialName = '', onCreated) => {
    profileCompanyCallbackRef.current = onCreated || null;
    setCompanyModal({
      isOpen: true,
      contactKey: null,
      submitting: false,
      values: {
        name: '',
        domain: '',
        website: '',
        phone: '',
        email: '',
        location: '',
        country: '',
        state: '',
        zip_code: '',
        address: ''
      }
    });
  };

  const handleSubmitCompanyModal = async () => {
    const hasContactKey = Boolean(companyModal.contactKey);
    const hasProfileCallback = Boolean(profileCompanyCallbackRef.current);
    if (!hasContactKey && !hasProfileCallback) return;
    const trimmedName = (companyModal.values.name || '').trim();
    if (!trimmedName) {
      alert('Company name is required.');
      return;
    }

    const payload = { name: trimmedName };
    ['domain', 'website', 'phone', 'email', 'location', 'country', 'state', 'zip_code', 'address'].forEach(field => {
      const value = (companyModal.values[field] || '').trim();
      if (value) {
        payload[field] = value;
      }
    });

    setCompanyModal(prev => ({ ...prev, submitting: true }));

    try {
      const response = await axios.post('/api/contact-management/companies', payload);
      if (response.data.success && response.data.company) {
        if (hasContactKey) {
          handleCompanySuggestionSelect(companyModal.contactKey, response.data.company);
          setCompanySearchTerm(trimmedName);
          await fetchCompanyOptions(trimmedName);
        }

        if (profileCompanyCallbackRef.current) {
          profileCompanyCallbackRef.current(response.data.company);
        }

        handleCloseCompanyModal();
      } else {
        alert(response.data.error || 'Failed to create company.');
      }
    } catch (error) {
      console.error('Failed to create company:', error);
      alert(error.response?.data?.error || 'Failed to create company. Please try again.');
    } finally {
      setCompanyModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const renderCompanyEditor = (contactKey) => {
    const value = contactInfoValues[contactKey]?.companyName || '';
    return (
      <div className="mt-2">
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Company</div>
        <div className="mt-1 flex gap-2 items-start">
          <div className="relative flex-1">
            <input
              type="text"
              value={value}
              onChange={(e) => handleCompanyInputChange(contactKey, e.target.value)}
              onFocus={(e) => {
                setActiveCompanyInput(contactKey);
                setCompanySearchTerm(e.target.value);
              }}
              onBlur={() => {
                setTimeout(() => {
                  setActiveCompanyInput(prev => (prev === contactKey ? null : prev));
                }, 200);
              }}
              className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Search or type a company name"
            />
            {activeCompanyInput === contactKey && (
              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-60 overflow-auto">
                {companySearchLoading ? (
                  <div className="px-3 py-2 text-xs text-gray-500">Searching companies...</div>
                ) : companyOptions.length > 0 ? (
                  companyOptions.map(company => (
                    <button
                      type="button"
                      key={company.id}
                      className="w-full px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-100"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleCompanySuggestionSelect(contactKey, company)}
                    >
                      {company.name}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-2 text-xs text-gray-500">No companies found</div>
                )}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => handleCreateCompany(contactKey)}
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50"
          >
            <Plus size={12} />
            Add New
          </button>
        </div>
      </div>
    );
  };

  const handleTogglePause = async (contactId, contactType, currentPauseState) => {
    try {
      setLoading(true);
      const response = await axios.put(`/api/contact-management/contacts/${contactId}/pause?contactType=${contactType}`, {
        pause: !currentPauseState
      });

      if (response.data.success) {
        // Update the local state to reflect the change
        setContacts(prev => prev.map(contact =>
          contact.id === contactId && contact.contact_type === contactType
            ? { ...contact, pause: !currentPauseState }
            : contact
        ));

        // Show success message
        console.log(`Contact ${!currentPauseState ? 'paused' : 'unpaused'} successfully`);
      }
    } catch (error) {
      console.error('Failed to toggle pause status:', error);
      alert('Failed to update pause status: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectContact = (contactId, contactType, contact) => {
    // Check if the contact belongs to the current user
    if (contact.user_id !== user?.id) {
      alert('You can only select contacts that belong to you.');
      return;
    }

    const contactKey = `${contactType}-${contactId}`;
    setSelectedContacts(prev => {
      const newSelected = new Map(prev);
      if (newSelected.has(contactKey)) {
        newSelected.delete(contactKey);
      } else {
        newSelected.set(contactKey, contact);
      }
      return newSelected;
    });
  };

  const handleSelectAll = () => {
    // Only select contacts that belong to the current user
    const userContacts = contacts.filter(contact => contact.user_id === user?.id);
    setSelectedContacts(prev => {
      const newSelected = new Map(prev);
      userContacts.forEach(contact => {
        const key = `${contact.contact_type}-${contact.id}`;
        if (selectAll) newSelected.delete(key);
        else newSelected.set(key, contact);
      });
      return newSelected;
    });
    setSelectAll(!selectAll);
  };


  const handleClearSelection = () => {
    setSelectedContacts(new Map());
    setSelectAll(false);
  };

  const bulkTagOptions = useMemo(() => {
    const tagNames = bulkTagModal.action === 'remove'
      ? Array.from(selectedContacts.values()).flatMap(contact => parseContactTagsValue(contact.tags))
      : availableTags;

    return [...new Map(tagNames
      .filter(tag => tag && String(tag).toLowerCase() !== 'block')
      .map(tag => [String(tag).toLowerCase(), String(tag)])).values()]
      .sort((first, second) => first.localeCompare(second));
  }, [availableTags, bulkTagModal.action, selectedContacts]);

  const handleToggleBulkTag = (tag) => {
    setBulkTagModal(previous => ({
      ...previous,
      selectedTags: previous.selectedTags.includes(tag)
        ? previous.selectedTags.filter(selectedTag => selectedTag !== tag)
        : [...previous.selectedTags, tag]
    }));
  };

  const handleBulkTagUpdate = async () => {
    const typedTags = bulkTagModal.action === 'add'
      ? bulkTagModal.newTags.split(',').map(tag => tag.trim()).filter(Boolean)
      : [];
    const tagsByName = new Map([...bulkTagModal.selectedTags, ...typedTags]
      .filter(tag => tag.toLowerCase() !== 'block')
      .map(tag => [tag.toLowerCase(), tag]));
    const tags = [...tagsByName.values()];

    if (tags.length === 0) {
      alert(`Select at least one tag to ${bulkTagModal.action}.`);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.put('/api/contact-management/contacts/bulk-tags', {
        contact_ids: Array.from(selectedContacts.values()).map(contact => contact.id),
        action: bulkTagModal.action,
        tags
      });

      if (response.data.success) {
        const updatedCount = response.data.updated_count || 0;
        alert(`${updatedCount} contact${updatedCount === 1 ? '' : 's'} updated.`);
        setBulkTagModal({ isOpen: false, action: 'add', selectedTags: [], newTags: '' });
        handleClearSelection();
        await Promise.all([loadContacts(), loadTags()]);
      }
    } catch (error) {
      console.error('Failed to update contact tags:', error);
      alert('Failed to update contact tags: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAllFiltered = async () => {
    try {
      setLoading(true);

      // Build the same parameters as the main loadContacts function, but without pagination
      const params = new URLSearchParams();

      // Add all current filters
      if (filters.producer) {
        params.append('producer', filters.producer);
      }
      if (filters.clientType !== 'All Client Types') {
        params.append('clientType', filters.clientType);
      }
      if (filters.source !== 'All Sources') {
        params.append('source', filters.source);
      }
      if (filters.name && filters.name.trim()) {
        params.append('name', filters.name.trim());
      }
      if (appliedContactFieldFilters.company) {
        params.append('company', appliedContactFieldFilters.company);
      }
      if (appliedContactFieldFilters.country) {
        params.append('country', appliedContactFieldFilters.country);
      }
      if (appliedContactFieldFilters.jobTitle) {
        params.append('jobTitle', appliedContactFieldFilters.jobTitle);
      }
      if (filters.tag !== 'All Tags') {
        params.append('tag', filters.tag);
      }
      if (filters.searchInNotes) {
        params.append('searchInNotes', 'true');
      }
      if (filters.showDuplicates) {
        params.append('showDuplicates', 'true');
      }

      // Request ALL contacts (no pagination) - use a high limit
      params.append('limit', '9999');
      params.append('page', '1');

      const response = await axios.get(`/api/contact-management/contacts?${params.toString()}`);

      if (response.data.success) {
        // Filter to only include contacts that belong to the current user
        const userContacts = response.data.contacts.filter(contact => contact.user_id === user?.id);

        const allContactsMap = new Map(
          userContacts.map(contact => [
            `${contact.contact_type}-${contact.id}`,
            contact
          ])
        );

        setSelectedContacts(allContactsMap);

        // Show feedback about how many contacts were selected
        if (userContacts.length > 0) {
          alert(`Selected ${userContacts.length} contacts across all pages that match your current filters.`);
        } else {
          alert('No contacts found that match your current filters and belong to you.');
        }
      }
    } catch (error) {
      console.error('Failed to select all filtered contacts:', error);
      alert('Failed to select all contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update selectAll state when selectedContacts changes
  useEffect(() => {
    if (contacts.length > 0) {
      // Only consider contacts that belong to the current user
      const userContacts = contacts.filter(contact => contact.user_id === user?.id);
      const allSelected = userContacts.length > 0 && userContacts.every(contact =>
        selectedContacts.has(`${contact.contact_type}-${contact.id}`)
      );
      setSelectAll(allSelected);
    }
  }, [selectedContacts, contacts, user?.id]);

  const getContactTypeColor = (contactType) => {
    switch (contactType) {
      case 'cold': return 'bg-blue-100 text-blue-700';
      case 'warm': return 'bg-orange-100 text-orange-700';
      case 'active': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getContactTypeLabel = (contactType) => {
    switch (contactType) {
      case 'cold': return 'Cold';
      case 'warm': return 'Warm';
      case 'active': return 'Active';
      default: return contactType;
    }
  };

  const renderTags = (tagsData) => {
    const getTagColor = (tag) => {
      if (tag === 'block') {
        return 'bg-red-100 text-red-800';
      }
      return 'bg-blue-100 text-blue-800';
    };
    const tags = parseContactTagsValue(tagsData);
    if (tags.length === 0) return <span className="text-xs text-gray-400">—</span>;

    return (
      <div className="flex items-center gap-1 max-w-full">
        {tags.slice(0, 2).map(tag => (
          <span key={tag} className={`px-2 py-1 ${getTagColor(tag)} text-xs rounded-full whitespace-nowrap max-w-24 truncate`} title={tag}>
            {tag === 'block' ? 'DNC' : tag}
          </span>
        ))}
        {tags.length > 2 && (
          <span className="text-xs text-gray-500" title={tags.slice(2).join(', ')}>+{tags.length - 2}</span>
        )}
      </div>
    );
  };

  const activeContactFilters = [
    filters.name && { key: 'name', label: `Search: ${filters.name}` },
    filters.company && { key: 'company', label: `Company: ${filters.company}` },
    filters.country && { key: 'country', label: `Country: ${filters.country}` },
    filters.jobTitle && { key: 'jobTitle', label: `Job title: ${filters.jobTitle}` },
    filters.clientType !== 'All Client Types' && { key: 'clientType', label: filters.clientType },
    filters.source !== 'All Sources' && { key: 'source', label: `Source: ${filters.source}` },
    filters.tag !== 'All Tags' && { key: 'tag', label: `Tag: ${filters.tag}` },
    filters.searchInNotes && { key: 'searchInNotes', label: 'Search notes' },
    filters.showDuplicates && { key: 'showDuplicates', label: 'Duplicates only' }
  ].filter(Boolean);

  // Use the enhanced CampaignModal component for selected contacts
  if (showCampaignModal) {
    let selectedContactsData = Array.from(selectedContacts.values());
    selectedContactsData = selectedContactsData.filter(c => {
      const current = contacts.find(contact => contact.id === c.id && contact.contact_type === c.contact_type);
      return current ? !current.pause : !c.pause;
    });
    return (
      <CampaignModal
        isOpen={showCampaignModal}
        onClose={() => setShowCampaignModal(false)}
        selectedContacts={selectedContactsData}
        templates={templates}
        gmailStatus={gmailStatus}
        loadCampaigns={() => { }}
        onSuccess={handleClearSelection}
        setShowContactPanel={setShowContactPanel}
        title="Create Campaign for Selected Contacts"
      />
    );
  }

  return (
    <div className="">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Contact Management</h2>
          <p className="text-gray-600">
            Total: {stats.total} contacts ({stats.coldLeads} cold, {stats.warmProspects} warm, {stats.activeClients} active)
          </p>
          {pagination.total > 0 && (
            <p className="text-sm text-gray-500">
              Showing {((currentPage - 1) * 15) + 1}-{Math.min(currentPage * 15, pagination.total)} of {pagination.total} contacts
            </p>
          )}
        </div>

        {/* Action buttons section */}
        <div className="space-y-3">
          {/* Selection info */}
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
            <span className="text-blue-700 font-medium">{selectedContacts.size} selected</span>
            <span className="text-blue-600">Select contacts for email campaigns or bulk actions</span>
          </div>

          {/* Campaign-related buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (selectedContacts.size === 0) {
                  alert('Please select contacts for email campaigns or bulk actions');
                  return;
                }
                console.log(selectedContacts.size);
                setShowCampaignModal(true);
              }}
              disabled={selectedContacts.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={16} />
              Create Campaign
            </button>
          </div>

          {/* Bulk action buttons */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleSelectAllFiltered}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CheckSquare size={16} />
              Select All Filtered
            </button>
            <button
              onClick={handleClearSelection}
              disabled={selectedContacts.size === 0}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle size={16} />
              Clear
            </button>
            <button
              onClick={() => setBulkTagModal({ isOpen: true, action: 'add', selectedTags: [], newTags: '' })}
              disabled={selectedContacts.size === 0 || loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Tags size={16} />
              Edit Tags
            </button>
            <button
              onClick={handleOpenMergeModal}
              disabled={selectedContacts.size !== 2}
              className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Merge size={16} />
              Merge Contacts
            </button>
            <button
              onClick={handleDeleteAllSelected}
              disabled={selectedContacts.size === 0 || loading}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 size={16} />
              Delete Selected
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={filters.producer}
              onChange={(e) => handleFilterChange('producer', e.target.value)}
              className="p-2 border border-gray-300 rounded-lg"
            >
              <option value="All Producers">All Producers</option>
              {producers.map(p => <option key={p.id || p} value={p.id}>{getProducerName(p)}</option>)}
            </select>
            <select
              value={filters.clientType}
              onChange={(e) => handleFilterChange('clientType', e.target.value)}
              className="p-2 border border-gray-300 rounded-lg"
            >
              <option>All Client Types</option>
              <option>Cold Clients</option>
              <option>Warm Clients</option>
              <option>Active Clients</option>
            </select>
            <select
              value={filters.source}
              onChange={(e) => handleFilterChange('source', e.target.value)}
              className="p-2 border border-gray-300 rounded-lg"
            >
              <option>All Sources</option>
              <option>scraping</option>
              <option>manual</option>
              <option>form</option>
              <option>email_forward</option>
              <option>zoho</option>
            </select>
            <TagFolderManager
              availableTags={availableTags}
              currentFilter={filters.tag}
              onFilterChange={handleFilterChange}
              onTagDeleteGlobally={handleDeleteTagGlobally}
              onTagsChanged={loadTags}
              producer={filters.producer}
              currentUser={user}
            />
            <input
              type="text"
              placeholder="Search..."
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
              className="p-2 border border-gray-300 rounded-lg min-w-48"
              aria-label="Search contacts"
            />
            <input
              type="text"
              placeholder="Filter company..."
              value={filters.company}
              onChange={(event) => handleFilterChange('company', event.target.value)}
              className="p-2 border border-gray-300 rounded-lg w-44"
              aria-label="Filter by company"
            />
            <input
              type="text"
              placeholder="Filter country..."
              value={filters.country}
              onChange={(event) => handleFilterChange('country', event.target.value)}
              className="p-2 border border-gray-300 rounded-lg w-40"
              aria-label="Filter by country"
            />
            <input
              type="text"
              placeholder="Filter job title..."
              value={filters.jobTitle}
              onChange={(event) => handleFilterChange('jobTitle', event.target.value)}
              className="p-2 border border-gray-300 rounded-lg w-44"
              aria-label="Filter by job title"
            />
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.searchInNotes}
                onChange={(e) => handleFilterChange('searchInNotes', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-600">Search in notes</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={filters.showDuplicates}
                onChange={(e) => handleFilterChange('showDuplicates', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-600">Show duplicates</span>
            </label>
            <button
              type="button"
              onClick={handleClearFilters}
              disabled={activeContactFilters.length === 0}
              className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear filters
            </button>
          </div>
          {activeContactFilters.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {activeContactFilters.map(filter => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => handleFilterChange(
                    filter.key,
                    ['clientType', 'source', 'tag'].includes(filter.key)
                      ? { clientType: 'All Client Types', source: 'All Sources', tag: 'All Tags' }[filter.key]
                      : ['searchInNotes', 'showDuplicates'].includes(filter.key) ? false : ''
                  )}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-full hover:bg-blue-100"
                  title="Remove filter"
                >
                  {filter.label}
                  <X size={12} />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
              <p>Loading contacts...</p>
            </div>
          ) : (
            <table className="w-full min-w-[1380px] table-fixed">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="w-12 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th
                    className="w-44 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('contact')}
                  >
                    <div className="flex items-center gap-1">
                      Contact
                      {sortConfig.key === 'contact' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUp size={14} /> :
                          <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="w-40 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('company')}
                  >
                    <div className="flex items-center gap-1">
                      Company
                      {sortConfig.key === 'company' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUp size={14} /> :
                          <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="w-40 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('job_title')}
                  >
                    <div className="flex items-center gap-1">
                      Job Title
                      {sortConfig.key === 'job_title' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUp size={14} /> :
                          <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="w-52 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-1">
                      Email
                      {sortConfig.key === 'email' && (
                        sortConfig.direction === 'asc' ?
                          <ChevronUp size={14} /> :
                          <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th
                    className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('country')}
                  >
                    <div className="flex items-center gap-1">
                      Country
                      {sortConfig.key === 'country' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </div>
                  </th>
                  <th className="w-20 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tags</th>
                  <th
                    className="w-20 px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('pause')}
                  >
                    Pause
                  </th>
                  <th
                    className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('due_date')}
                  >
                    Due Date
                  </th>
                  <th className="w-28 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                  <th className="w-36 px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContacts.map(contact => {
                  return (
                    <tr key={`${contact.contact_type}-${contact.id}`} className="h-14 hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedContacts.has(`${contact.contact_type}-${contact.id}`)}
                        onChange={() => handleSelectContact(contact.id, contact.contact_type, contact)}
                        disabled={contact.user_id !== user?.id}
                        className={`rounded text-blue-600 focus:ring-blue-500 ${contact.user_id !== user?.id
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                          : 'border-gray-300'
                          }`}
                        title={contact.user_id !== user?.id ? 'You can only select your own contacts' : ''}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          {editingContactInfo[`${contact.contact_type}-${contact.id}`] ? (
                            <input
                              type="text"
                              value={contactInfoValues[`${contact.contact_type}-${contact.id}`]?.fullName || ''}
                              onChange={(e) => setContactInfoValues(prev => ({
                                ...prev,
                                [`${contact.contact_type}-${contact.id}`]: {
                                  ...prev[`${contact.contact_type}-${contact.id}`],
                                  fullName: e.target.value
                                }
                              }))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium text-gray-900"
                              placeholder="Full name"
                            />
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleOpenContactProfile(contact)}
                              className="text-left text-sm font-medium text-gray-900 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                            >
                              {contact.first_name} {contact.last_name}
                            </button>
                          )}
                          <div className="hidden">{contact.job_title || '—'}</div>
                          {editingContactInfo[`${contact.contact_type}-${contact.id}`]
                            ? renderCompanyEditor(`${contact.contact_type}-${contact.id}`)
                            : (
                              contact.company && (
                                (contact.companies_id ?? contact.company_id) ? (
                                  <button
                                    type="button"
                                    onClick={() => handleViewCompanyDetails(contact.companies_id ?? contact.company_id)}
                                    className="hidden"
                                    title="View company details"
                                  >
                                    {contact.company}
                                  </button>
                                ) : (
                                  <div className="hidden">{contact.company}</div>
                                )
                              )
                            )}
                          {editingContactInfo[`${contact.contact_type}-${contact.id}`] ? (
                            <div className="mt-1 space-y-2">
                              <input
                                type="email"
                                value={contactInfoValues[`${contact.contact_type}-${contact.id}`]?.email || ''}
                                onChange={(e) => setContactInfoValues(prev => ({
                                  ...prev,
                                  [`${contact.contact_type}-${contact.id}`]: {
                                    ...prev[`${contact.contact_type}-${contact.id}`],
                                    email: e.target.value
                                  }
                                }))}
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                placeholder="Email address"
                              />
                            </div>
                          ) : (
                            <div className="hidden">{contact.email}</div>
                          )}
                          {contact.phone && <div className="hidden">{contact.phone}</div>}
                          {contact.location && <div className="hidden">{contact.location}</div>}
                          {contact.country && <div className="hidden">{contact.country}</div>}
                        </div>
                        {!editingContactInfo[`${contact.contact_type}-${contact.id}`] ? (
                          <button
                            onClick={() => handleOpenContactProfile(contact)}
                            className={`p-1 flex-shrink-0 ${contact.user_id !== user?.id
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-gray-400 hover:text-blue-600'
                              }`}
                            disabled={loading || contact.user_id !== user?.id}
                            title={contact.user_id !== user?.id ? 'You can only view your own contacts' : 'View full contact profile'}
                          >
                            <Edit3 size={14} />
                          </button>
                        ) : (
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => handleUpdateContactInfo(contact.id, contact.contact_type)}
                              className="p-1 text-green-600 hover:text-green-800"
                              disabled={loading}
                              title="Save all changes"
                            >
                              <CheckCircle size={14} />
                            </button>
                            <button
                              onClick={() => handleCancelContactInfoEdit(contact.id, contact.contact_type)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                              disabled={loading}
                              title="Cancel all changes"
                            >
                              <XCircle size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-700 truncate" title={contact.company || ''}>
                      {contact.company ? (
                        (contact.companies_id ?? contact.company_id) ? (
                          <button
                            type="button"
                            onClick={() => handleViewCompanyDetails(contact.companies_id ?? contact.company_id)}
                            className="max-w-full text-left text-blue-600 hover:underline truncate"
                          >
                            {contact.company}
                          </button>
                        ) : contact.company
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 truncate" title={contact.job_title || ''}>
                      {contact.job_title || '—'}
                    </td>
                    <td className="px-3 py-2 text-sm truncate" title={contact.email || ''}>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-blue-600 hover:underline">{contact.email}</a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-600 truncate" title={contact.country || ''}>
                      {contact.country || '—'}
                    </td>
                    {/*<td className="px-4 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {contact.is_super_engager && (
                          <div className="flex items-center gap-1 text-orange-600">
                            <span>🔥</span>
                            <span className="text-xs font-medium">Super Engager</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <div className="flex items-center gap-1">
                            <span>📊</span>
                            <span>{contact.lifetime_engagement_score || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {(() => {
                              const momentum = contact.momentum_score || 0;
                              if (momentum > 50) return <span className="text-green-600">↗️</span>;
                              if (momentum > 0) return <span className="text-blue-600">→</span>;
                              if (momentum < 0) return <span className="text-red-600">↘️</span>;
                              return <span className="text-gray-400">→</span>;
                            })()}
                            <span>{contact.momentum_score || 0}</span>
                          </div>
                        </div>
                      </div>
                    </td>*/}
                    <td className="hidden">
                      <div className="text-sm text-gray-900">{contact.source}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getContactTypeColor(contact.contact_type)}`}>
                        {getContactTypeLabel(contact.contact_type)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {editingContactInfo[`${contact.contact_type}-${contact.id}`] ? (
                        <div className="flex flex-wrap gap-1 items-center max-w-full">
                          {(contactInfoValues[`${contact.contact_type}-${contact.id}`]?.tags || []).map((tag, index) => (
                            <span
                              key={index}
                              className={`inline-flex items-center px-2 py-1 text-xs rounded-full whitespace-nowrap ${tag === 'block' ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}
                            >
                              {tag == "block" ? "DNC" : tag}
                              {tag !== 'block' && (
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Are you sure you want to remove the "${tag}" tag from this contact?`)) {
                                      const key = `${contact.contact_type}-${contact.id}`;
                                      const currentTags = contactInfoValues[key]?.tags || [];
                                      const newTags = currentTags.filter((_, i) => i !== index);
                                      setContactInfoValues(prev => ({
                                        ...prev,
                                        [key]: {
                                          ...prev[key],
                                          tags: newTags
                                        }
                                      }));
                                    }
                                  }}
                                  className="ml-1 text-blue-600 hover:text-blue-800"
                                >
                                  ×
                                </button>
                              )}
                            </span>
                          ))}
                          <button
                            onClick={() => {
                              const key = `${contact.contact_type}-${contact.id}`;
                              const newTag = prompt('Enter new tag:');
                              if (newTag && newTag.trim()) {
                                const currentTags = contactInfoValues[key]?.tags || [];
                                if (!currentTags.includes(newTag.trim())) {
                                  setContactInfoValues(prev => ({
                                    ...prev,
                                    [key]: {
                                      ...prev[key],
                                      tags: [...currentTags, newTag.trim()]
                                    }
                                  }));
                                }
                              }
                            }}
                            className="inline-flex items-center justify-center w-6 h-6 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50"
                            title="Add tag"
                          >
                            +
                          </button>
                        </div>
                      ) : (
                        <div className="max-w-full">
                          {renderTags(contact.tags)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={contact.pause || false}
                        onChange={() => handleTogglePause(contact.id, contact.contact_type, contact.pause)}
                        disabled={loading}
                        className="rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                      {contact.due_date && new Date(contact.due_date).toLocaleDateString()}
                    </td>
                    <td className="hidden">
                      {contact.zoho_due_date_note}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        {editingContactInfo[`${contact.contact_type}-${contact.id}`] ? (
                          <select
                            value={contactInfoValues[`${contact.contact_type}-${contact.id}`]?.assignment?.id}
                            onChange={(e) => setContactInfoValues(prev => ({
                              ...prev,
                              [`${contact.contact_type}-${contact.id}`]: {
                                ...prev[`${contact.contact_type}-${contact.id}`],
                                assignment: producers.find(p => p.id === parseInt(e.target.value)) || null
                              }
                            }))}
                            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="Unassigned">Unassigned</option>
                            {producers.map(producer => (
                              <option key={producer.id} value={producer.id}>{producer.display_name || producer.username}</option>
                            ))}
                          </select>
                        ) : (
                          <span>{contact.producer_name?.display_name || contact.producer_name?.username || 'Unassigned'}</span>
                        )}
                      </div>
                    </td>
                    <td className="hidden">
                      {new Date(contact.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex gap-1 items-center flex-nowrap">
                        {contact.contact_type === 'cold' && (
                          <button
                            onClick={() => handlePromoteContact(contact.id, 'cold', 'warm')}
                            className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200"
                            disabled={loading}
                          >
                            → Warm
                          </button>
                        )}
                        {contact.contact_type === 'warm' && (
                          <>
                            <button
                              onClick={() => handlePromoteContact(contact.id, 'warm', 'cold')}
                              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded hover:bg-gray-200"
                              disabled={loading}
                            >
                              → Cold
                            </button>
                            <button
                              onClick={() => handlePromoteContact(contact.id, 'warm', 'active')}
                              className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded hover:bg-green-200"
                              disabled={loading}
                            >
                              → Active
                            </button>
                          </>
                        )}
                        {contact.contact_type === 'active' && (
                          <button
                            onClick={() => handlePromoteContact(contact.id, 'active', 'warm')}
                            className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded hover:bg-orange-200"
                            disabled={loading}
                          >
                            → Warm
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteContact(contact.id, contact.contact_type, contact.source, contact.zoho_id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                          disabled={loading}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                <div className="flex gap-1 items-center">
                  {/* First page */}
                  {currentPage > 3 && pagination.totalPages > 5 && (
                    <>
                      <button
                        onClick={() => setCurrentPage(1)}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        1
                      </button>
                      {currentPage > 4 && <span className="px-2 text-gray-400">...</span>}
                    </>
                  )}

                  {/* Page numbers around current page */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-2 text-sm rounded-lg ${pageNum === currentPage
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}

                  {/* Last page */}
                  {currentPage < pagination.totalPages - 2 && pagination.totalPages > 5 && (
                    <>
                      {currentPage < pagination.totalPages - 3 && <span className="px-2 text-gray-400">...</span>}
                      <button
                        onClick={() => setCurrentPage(pagination.totalPages)}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {pagination.totalPages}
                      </button>
                    </>
                  )}

                  {/* Page input for direct navigation */}
                  {pagination.totalPages > 10 && (
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-sm text-gray-600">Go to:</span>
                      <input
                        type="number"
                        min="1"
                        max={pagination.totalPages}
                        value={currentPage}
                        onChange={(e) => {
                          const page = parseInt(e.target.value);
                          if (page >= 1 && page <= pagination.totalPages) {
                            setCurrentPage(page);
                          }
                        }}
                        className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                  disabled={currentPage === pagination.totalPages}
                  className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>



      {bulkTagModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-lg bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Edit Contact Tags</h3>
                <p className="text-sm text-gray-500">{selectedContacts.size} contacts selected</p>
              </div>
              <button
                type="button"
                onClick={() => setBulkTagModal({ isOpen: false, action: 'add', selectedTags: [], newTags: '' })}
                className="p-1 text-gray-400 hover:text-gray-600"
                aria-label="Close bulk tag editor"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg">
                {['add', 'remove'].map(action => (
                  <button
                    key={action}
                    type="button"
                    onClick={() => setBulkTagModal(previous => ({
                      ...previous,
                      action,
                      selectedTags: [],
                      newTags: ''
                    }))}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${bulkTagModal.action === action
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                      }`}
                  >
                    {action === 'add' ? 'Add Tags' : 'Remove Tags'}
                  </button>
                ))}
              </div>

              {bulkTagModal.action === 'add' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New tags</label>
                  <input
                    type="text"
                    value={bulkTagModal.newTags}
                    onChange={(event) => setBulkTagModal(previous => ({ ...previous, newTags: event.target.value }))}
                    placeholder="Enter tags separated by commas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {bulkTagModal.action === 'add' ? 'Existing tags' : 'Tags on selected contacts'}
                </p>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {bulkTagOptions.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {bulkTagOptions.map(tag => (
                        <label
                          key={tag}
                          className={`flex items-center gap-2 p-2 text-sm rounded-md cursor-pointer ${bulkTagModal.selectedTags.includes(tag)
                            ? 'bg-blue-50 text-blue-800'
                            : 'hover:bg-gray-50 text-gray-700'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={bulkTagModal.selectedTags.includes(tag)}
                            onChange={() => handleToggleBulkTag(tag)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="truncate" title={tag}>{tag}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <p className="py-4 text-sm text-center text-gray-500">
                      {bulkTagModal.action === 'remove'
                        ? 'The selected contacts have no removable tags.'
                        : 'No existing tags are available.'}
                    </p>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">DNC tags are protected and cannot be changed here.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-lg">
              <button
                type="button"
                onClick={() => setBulkTagModal({ isOpen: false, action: 'add', selectedTags: [], newTags: '' })}
                className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkTagUpdate}
                disabled={loading || (
                  bulkTagModal.selectedTags.length === 0
                  && (bulkTagModal.action !== 'add' || !bulkTagModal.newTags.trim())
                )}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : bulkTagModal.action === 'add' ? 'Add Tags' : 'Remove Tags'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Editing Modal */}
      <NotesModal
        isOpen={notesModal.isOpen}
        contactId={notesModal.contactId}
        contactType={notesModal.contactType}
        currentNotes={notesModal.currentNotes}
        onClose={handleCloseNotesModal}
        onSave={handleSaveNotes}
        openNewNoteForm={notesModal.openNewNoteForm}
      />

      <CompanyCreateModal
        isOpen={companyModal.isOpen}
        values={companyModal.values}
        submitting={companyModal.submitting}
        onClose={handleCloseCompanyModal}
        onSubmit={handleSubmitCompanyModal}
        onChange={handleCompanyModalFieldChange}
      />

      <CompanyDetailsModal
        isOpen={companyDetailsModal.isOpen}
        loading={companyDetailsModal.loading}
        company={companyDetailsModal.data}
        editing={companyDetailsModal.editing}
        values={companyDetailsModal.values}
        saving={companyDetailsModal.saving}
        error={companyDetailsModal.error}
        onClose={handleCloseCompanyDetailsModal}
        onStartEdit={handleStartCompanyDetailsEdit}
        onCancelEdit={handleCancelCompanyDetailsEdit}
        onFieldChange={handleCompanyDetailsFieldChange}
        onSave={handleSaveCompanyDetails}
      />

      {contactProfileModal.isOpen && (
        <ContactProfileModal
          contact={contactProfileModal.contact}
          profileData={contactProfileModal.data}
          loading={contactProfileModal.loading}
          error={contactProfileModal.error}
          onClose={handleCloseContactProfile}
          onContactUpdate={handleContactProfileUpdate}
          onRequestCreateCompany={handleProfileRequestNewCompany}
          currentUserId={user?.id}
          currentUserRole={user?.role}
          producers={producers}
          onShowAllNotes={handleOpenNotesModal}
        />
      )}

      {/* Merge Contacts Modal */}
      {mergeModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-4">Merge Contacts</h3>
            <p className="text-gray-600 mb-6">Choose which information to keep for the merged contact. The contact on the left will be kept, and the one on the right will be deleted.</p>

            <div className="grid grid-cols-2 gap-6">
              {/* Contact 1 (Primary) */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-green-600 mb-3">Primary Contact (Will be kept)</h4>
                <div className="space-y-3">
                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name', label: 'Last Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'job_title', label: 'Job Title' },
                    { key: 'company', label: 'Company' },
                    { key: 'location', label: 'Location' },
                    { key: 'country', label: 'Country' },
                    { key: 'tags', label: 'Tags' },
                    { key: 'notes', label: 'Notes' }
                  ].map(field => (
                    <div key={field.key} className="flex items-center gap-3">
                      <input
                        type="radio"
                        name={field.key}
                        checked={mergeModal.mergedContact[field.key] === mergeModal.contact1[field.key]}
                        onChange={() => setMergeModal(prev => ({
                          ...prev,
                          mergedContact: {
                            ...prev.mergedContact,
                            [field.key]: prev.contact1[field.key]
                          }
                        }))}
                        className="text-blue-600"
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                        <div className="text-sm text-gray-900 break-words">
                          {field.key === 'tags' ? (
                            mergeModal.contact1[field.key] ?
                              JSON.parse(mergeModal.contact1[field.key]).join(', ') :
                              'None'
                          ) : (
                            mergeModal.contact1[field.key] || 'None'
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact 2 (Secondary) */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium text-red-600 mb-3">Secondary Contact (Will be deleted)</h4>
                <div className="space-y-3">
                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name', label: 'Last Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'phone', label: 'Phone' },
                    { key: 'job_title', label: 'Job Title' },
                    { key: 'company', label: 'Company' },
                    { key: 'location', label: 'Location' },
                    { key: 'country', label: 'Country' },
                    { key: 'tags', label: 'Tags' },
                    { key: 'notes', label: 'Notes' }
                  ].map(field => (
                    <div key={field.key} className="flex items-center gap-3">
                      <input
                        type="radio"
                        name={field.key}
                        checked={mergeModal.mergedContact[field.key] === mergeModal.contact2[field.key]}
                        onChange={() => setMergeModal(prev => ({
                          ...prev,
                          mergedContact: {
                            ...prev.mergedContact,
                            [field.key]: prev.contact2[field.key]
                          }
                        }))}
                        className="text-blue-600"
                      />
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                        <div className="text-sm text-gray-900 break-words">
                          {field.key === 'tags' ? (
                            mergeModal.contact2[field.key] ?
                              JSON.parse(mergeModal.contact2[field.key]).join(', ') :
                              'None'
                          ) : (
                            mergeModal.contact2[field.key] || 'None'
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={handleCloseMergeModal}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleMergeContacts}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? 'Merging...' : 'Merge Contacts'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const ResponseHandler = ({ gmailStatus }) => {
  const [responseStats, setResponseStats] = useState({
    notInterested: 0,
    outOfOffice: 0,
    forwarded: 0,
    bounced: 0
  });
  const [recentResponses, setRecentResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeResponseModal, setActiveResponseModal] = useState(null);

  useEffect(() => {
    loadResponseStats();
    loadRecentResponses();
  }, []);

  const loadResponseStats = async () => {
    try {
      const response = await axios.get('/api/email-campaigns/response-stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        setResponseStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load response stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentResponses = async () => {
    try {
      const response = await axios.get('/api/email-campaigns/recent-responses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.data.success) {
        setRecentResponses(response.data.responses);
      }
    } catch (error) {
      console.error('Failed to load recent responses:', error);
    }
  };

  if (activeResponseModal) {
    return (
      <BouncedContactsModal
        isOpen={Boolean(activeResponseModal)}
        type={activeResponseModal}
        onClose={() => setActiveResponseModal(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Response Handling & Automation</h2>

      <div className="grid grid-cols-4 gap-4">
        <div className="bg-red-50 p-4 rounded-lg border border-red-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-red-900">Not Interested</h3>
            <XCircle className="text-red-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-red-700">
            {loading ? '...' : responseStats.notInterested}
          </p>
          <p className="text-sm text-red-600">To be deleted from Zoho</p>
          <button
            onClick={() => setActiveResponseModal('not_interested')}
            className="mt-3 w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 text-sm"
          >
            Process Deletions
          </button>
        </div>

        <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-orange-900">Out of Office</h3>
            <Clock className="text-orange-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-orange-700">
            {loading ? '...' : responseStats.outOfOffice}
          </p>
          <p className="text-sm text-orange-600">Campaigns paused</p>
          <button
            onClick={() => setActiveResponseModal('out_of_office')}
            className="mt-3 w-full bg-orange-600 text-white py-2 rounded-lg hover:bg-orange-700 text-sm"
          >
            View OOO List
          </button>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-blue-900">Forwarded Contacts</h3>
            <Forward className="text-blue-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-blue-700">
            {loading ? '...' : responseStats.forwarded}
          </p>
          <p className="text-sm text-blue-600">New leads generated</p>
          <button
            onClick={() => setActiveResponseModal('forwarded')}
            className="mt-3 w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 text-sm"
          >
            Process New Leads
          </button>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-medium text-yellow-900">Bounced Emails</h3>
            <AlertCircle className="text-yellow-600" size={20} />
          </div>
          <p className="text-2xl font-bold text-yellow-700">
            {loading ? '...' : responseStats.bounced}
          </p>
          <p className="text-sm text-yellow-600">Failed deliveries to clean up</p>
          <button
            onClick={() => setActiveResponseModal('bounced')}
            className="mt-3 w-full bg-yellow-600 text-white py-2 rounded-lg hover:bg-yellow-700 text-sm"
          >
            Review Bounced
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <h3 className="font-semibold text-gray-900 mb-4">Recent Responses</h3>
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              <p className="text-gray-500">Loading responses...</p>
            </div>
          ) : recentResponses.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No recent responses found</p>
            </div>
          ) : (
            recentResponses.map(response => (
              <div key={response.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">
                    {response.first_name} {response.last_name} - {response.response_type}
                  </p>
                  <p className="text-sm text-gray-600">
                    {response.last_response_reason || response.last_error_message ||
                      (response.status === 'out_of_office' ? 'Out of office auto-response' :
                        response.status === 'forwarded' ? `Forwarded to: ${response.email}` :
                          'Email response received')}
                  </p>
                  <p className="text-xs text-gray-400">Campaign: {response.campaign_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {response.last_response_received_at ?
                      new Date(response.last_response_received_at).toLocaleDateString() :
                      new Date(response.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const WebsiteForms = ({ websiteForms, producerColors }) => {
  const [formStats, setFormStats] = useState({
    quoteRequests: 0,
    meetingRequests: 0,
    copyrightRequests: 0,
    totalSubmissions: 0
  });
  const [recentSubmissions, setRecentSubmissions] = useState([]);
  const [autoResponseTemplates, setAutoResponseTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditTemplate, setShowEditTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState({
    form_type: '',
    template_name: '',
    subject: '',
    body: ''
  });

  useEffect(() => {
    loadFormStats();
    loadRecentSubmissions();
    loadAutoResponseTemplates();
  }, []);

  const loadFormStats = async () => {
    try {
      const response = await axios.get('/api/website-forms/stats');
      if (response.data.success) {
        setFormStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to load website form stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentSubmissions = async () => {
    try {
      const response = await axios.get('/api/website-forms/recent-submissions');
      if (response.data.success) {
        setRecentSubmissions(response.data.submissions);
      }
    } catch (error) {
      console.error('Failed to load recent submissions:', error);
    }
  };

  const loadAutoResponseTemplates = async () => {
    try {
      const response = await axios.get('/api/website-forms/auto-response-templates');
      if (response.data.success) {
        setAutoResponseTemplates(response.data.templates);
      }
    } catch (error) {
      console.error('Failed to load auto-response templates:', error);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({
      form_type: template.form_type,
      template_name: template.template_name,
      subject: template.subject,
      body: template.body
    });
    setShowEditTemplate(template.form_type);
  };

  const handleSaveTemplate = async () => {
    try {
      const response = await axios.post('/api/website-forms/auto-response-templates', editingTemplate);
      if (response.data.success) {
        alert('Template saved successfully!');
        setShowEditTemplate(null);
        loadAutoResponseTemplates();
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      alert('Failed to save template: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRevertTemplate = async (formType) => {
    if (!window.confirm('Are you sure you want to revert to the global default template?')) return;

    try {
      const response = await axios.delete(`/api/website-forms/auto-response-templates/${formType}`);
      if (response.data.success) {
        alert('Template reverted to global default!');
        loadAutoResponseTemplates();
      }
    } catch (error) {
      console.error('Failed to revert template:', error);
      alert('Failed to revert template: ' + (error.response?.data?.error || error.message));
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900">Website Forms - SQL Integration</h2>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="font-medium text-gray-900 mb-2">Quote Requests</h3>
          <p className="text-3xl font-bold text-blue-600">
            {loading ? '...' : formStats.quoteRequests}
          </p>
          <p className="text-sm text-gray-600">Total submissions</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="font-medium text-gray-900 mb-2">Meeting Requests</h3>
          <p className="text-3xl font-bold text-green-600">
            {loading ? '...' : formStats.meetingRequests}
          </p>
          <p className="text-sm text-gray-600">Total submissions</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h3 className="font-medium text-gray-900 mb-2">Copyright Requests</h3>
          <p className="text-3xl font-bold text-purple-600">
            {loading ? '...' : formStats.copyrightRequests}
          </p>
          <p className="text-sm text-gray-600">Total submissions</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-semibold text-gray-900">Recent Form Submissions</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Form Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact Info</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                    <p className="text-gray-500">Loading submissions...</p>
                  </td>
                </tr>
              ) : recentSubmissions.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center">
                    <p className="text-gray-500">No recent form submissions</p>
                  </td>
                </tr>
              ) : (
                recentSubmissions.map(form => (
                  <tr key={form.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${form.form_type === 'quote_request' ? 'bg-blue-100 text-blue-700' :
                        form.form_type === 'meeting_request' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                        {form.form_type_display}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {form.first_name} {form.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{form.company || 'N/A'}</div>
                      <div className="text-xs text-gray-400">{form.email}</div>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">
                        {form.producer_name}
                      </span>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold text-gray-900">Auto-Response Templates</h3>
          <p className="text-xs text-gray-500">
            Customize templates for your responses. Global defaults apply if no custom template exists.
          </p>
        </div>

        {showEditTemplate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-bold">Edit Template - {editingTemplate.form_type.replace('_', ' ').toUpperCase()}</h4>
                <button
                  onClick={() => setShowEditTemplate(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Template Name</label>
                  <input
                    type="text"
                    value={editingTemplate.template_name}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, template_name: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="e.g., My Quote Response Template"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject Line</label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Use {FIRST_NAME}, {COMPANY}, etc. for personalization"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Body</label>
                  <textarea
                    value={editingTemplate.body}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, body: e.target.value }))}
                    rows={10}
                    className="w-full p-3 border border-gray-300 rounded-lg"
                    placeholder="Use variables like {FIRST_NAME}, {PROJECT_TYPE}, {BUDGET}, etc."
                  />
                </div>

                <div className="text-xs text-gray-500 p-3 bg-gray-50 rounded">
                  <strong>Available variables:</strong> {'{FIRST_NAME}, {LAST_NAME}, {COMPANY}, {EMAIL}, {PHONE}'}
                  {editingTemplate.form_type === 'quote_request' && ', {PROJECT_TYPE}, {BUDGET}, {TIMELINE}, {DESCRIPTION}'}
                  {editingTemplate.form_type === 'meeting_request' && ', {MEETING_TYPE}, {PREFERRED_DATE}, {PREFERRED_TIME}, {TOPICS}'}
                  {editingTemplate.form_type === 'copyright_request' && ', {WORK_TYPE}, {CONTENT_DESCRIPTION}, {USAGE_INTENT}, {DEADLINE}'}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowEditTemplate(null)}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Template
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-4">
          {loading ? (
            <div className="text-center py-4">
              <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
              <p className="text-gray-500">Loading templates...</p>
            </div>
          ) : (
            autoResponseTemplates.map(template => (
              <div key={template.form_type} className={`p-4 rounded-lg border-2 ${template.form_type === 'quote_request' ? 'bg-blue-50 border-blue-200' :
                template.form_type === 'meeting_request' ? 'bg-green-50 border-green-200' :
                  'bg-purple-50 border-purple-200'
                }`}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className={`font-medium ${template.form_type === 'quote_request' ? 'text-blue-900' :
                      template.form_type === 'meeting_request' ? 'text-green-900' :
                        'text-purple-900'
                      }`}>
                      {template.form_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {template.template_type === 'user_custom' ? 'Custom Template' : 'Global Default'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="text-xs bg-white px-2 py-1 rounded border hover:bg-gray-50"
                    >
                      {template.template_type === 'user_custom' ? 'Edit' : 'Customize'}
                    </button>
                    {template.template_type === 'user_custom' && (
                      <button
                        onClick={() => handleRevertTemplate(template.form_type)}
                        className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded hover:bg-red-200"
                      >
                        Revert
                      </button>
                    )}
                  </div>
                </div>
                <div className="mb-2">
                  <p className="text-xs font-medium text-gray-700">Subject:</p>
                  <p className="text-sm text-gray-800">{template.subject}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-700">Preview:</p>
                  <p className={`text-sm mt-1 line-clamp-2 ${template.form_type === 'quote_request' ? 'text-blue-700' :
                    template.form_type === 'meeting_request' ? 'text-green-700' :
                      'text-purple-700'
                    }`}>
                    {template.body.split('\n')[0]}...
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    loadNotifications();
    loadUnreadCount();

    // Refresh notifications every 30 seconds
    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/notifications');
      if (response.data.success) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await axios.get('/api/notifications/count');
      if (response.data.success) {
        setUnreadCount(response.data.unreadCount);
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await axios.put(`/api/notifications/${notificationId}/read`);
      if (response.data.success) {
        // Update local state
        setNotifications(prev =>
          prev.map(notification =>
            notification.id === notificationId
              ? { ...notification, is_read: 1 }
              : notification
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success': return <CheckSquare className="text-green-500" size={20} />;
      case 'warning': return <AlertTriangle className="text-yellow-500" size={20} />;
      case 'error': return <XCircle className="text-red-500" size={20} />;
      default: return <Info className="text-blue-500" size={20} />;
    }
  };

  const getNotificationBg = (type) => {
    switch (type) {
      case 'success': return 'bg-green-50 border-green-200';
      case 'warning': return 'bg-yellow-50 border-yellow-200';
      case 'error': return 'bg-red-50 border-red-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className="relative">
      {/* Notification Bell Button */}
      <button
        onClick={() => {
          setShowNotifications(!showNotifications);
          if (!showNotifications) {
            loadNotifications();
          }
        }}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Dropdown */}
      {showNotifications && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <button
              onClick={() => setShowNotifications(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={20} />
            </button>
          </div>

          <div className="overflow-y-auto max-h-80">
            {loading ? (
              <div className="p-4 text-center">
                <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                <p className="text-gray-500">Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="mx-auto mb-2 text-gray-400" size={32} />
                <p className="text-gray-500">No notifications</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map(notification => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${notification.is_read ? 'opacity-75' : ''
                      }`}
                    onClick={() => {
                      if (!notification.is_read) {
                        markAsRead(notification.id);
                      }
                    }}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4 className={`text-sm font-medium ${notification.is_read ? 'text-gray-600' : 'text-gray-900'
                            }`}>
                            {notification.title}
                          </h4>
                          {!notification.is_read && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 ml-2 mt-1"></span>
                          )}
                        </div>
                        <p className={`text-sm mt-1 ${notification.is_read ? 'text-gray-500' : 'text-gray-700'
                          }`}>
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          {new Date(notification.created_at).toLocaleDateString()} at{' '}
                          {new Date(notification.created_at).toLocaleTimeString()}
                          {notification.is_global && (
                            <span className="ml-2 px-1 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                              Global
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  // Mark all as read
                  notifications.filter(n => !n.is_read).forEach(n => markAsRead(n.id));
                }}
                className="text-xs text-blue-600 hover:text-blue-800"
                disabled={unreadCount === 0}
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ScrapingPreview = ({ gmailStatus }) => {
  const [previewLeads, setPreviewLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sendingLeads, setSendingLeads] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 5
  });
  const [editingEmails, setEditingEmails] = useState(new Set());
  const [savingEmails, setSavingEmails] = useState(new Set());

  // Template selection state
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState({});

  // Email template preview modal state
  const [emailTemplateModal, setEmailTemplateModal] = useState({
    isOpen: false,
    lead: null
  });

  // Bulk selection and search state
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [allSelected, setAllSelected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const fetchPreviewLeads = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/scraping-preview/preview?page=${currentPage}&limit=5${debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ''}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setPreviewLeads(result.previews || []);
        setPagination(result.pagination || {
          total: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 5
        });

        // Initialize selectedTemplates with existing email_template_id values
        const initialSelectedTemplates = {};
        (result.previews || []).forEach(lead => {
          if (lead.email_template_id) {
            initialSelectedTemplates[lead.id] = lead.email_template_id.toString();
          }
        });
        setSelectedTemplates(prev => ({ ...prev, ...initialSelectedTemplates }));
      }
    } catch (error) {
      console.error("Failed to fetch preview leads:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearchQuery]);

  // Debounce search query and reset to page 1 when searching
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Clear selected leads when page changes or search changes
  useEffect(() => {
    setSelectedLeads(new Set());
    setAllSelected(false);
  }, [currentPage, debouncedSearchQuery]);

  // Function to fetch all lead IDs for selection
  const fetchAllLeadIds = async () => {
    try {
      const response = await fetch(`/api/scraping-preview/preview?page=1&limit=10000${debouncedSearchQuery ? `&search=${encodeURIComponent(debouncedSearchQuery)}` : ''}`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        return result.previews || [];
      }
      return [];
    } catch (error) {
      console.error("Failed to fetch all lead IDs:", error);
      return [];
    }
  };

  // Bulk selection handlers
  const handleSelectAll = async (checked) => {
    if (checked) {
      // Select all leads across all pages
      const allLeads = await fetchAllLeadIds();
      const allLeadIds = new Set(allLeads.map(lead => lead.id));
      setSelectedLeads(allLeadIds);
      setAllSelected(true);
    } else {
      setSelectedLeads(new Set());
      setAllSelected(false);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    const newSelected = new Set(selectedLeads);
    if (checked) {
      newSelected.add(leadId);
    } else {
      newSelected.delete(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleBulkMoveToColdLeads = async () => {
    if (selectedLeads.size === 0) {
      alert('Please select leads to move');
      return;
    }

    const selectedArray = Array.from(selectedLeads);

    try {
      const response = await fetch("/api/scraping-preview/move-to-cold-leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        },
        body: JSON.stringify({
          previewIds: selectedArray
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`Successfully moved ${result.movedCount} lead(s) to cold leads${result.skippedCount > 0 ? `, ${result.skippedCount} already moved` : ''}`);
        setSelectedLeads(new Set()); // Clear selection
        fetchPreviewLeads(); // Refresh to update the table
      } else {
        alert(`Failed to move leads: ${result.error || result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error("Bulk move to cold leads error:", error);
      alert(`Failed to move leads: ${error.message || 'Network or server error'}`);
    }
  };

  // Email enrichment function using bulk-match endpoint
  const handleEmailEnrichment = async (lead) => {
    try {
      // Call the updated bulk-match endpoint to enrich this single contact
      const response = await fetch('/api/apollo/bulk-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          jobId: lead.scraping_job_id, // Use the job ID from the lead
          selectedPeople: [{
            apollo_person_id: lead.apollo_person_id,
            first_name: lead.first_name,
            last_name: lead.last_name,
            company: lead.company,
            company_domain: lead.company_domain || lead.company // fallback to company name if no domain
          }],
          updateScrapingPreview: true // Enable scraping_preview table update
        })
      });

      const result = await response.json();

      if (response.ok && result.enrichedPeople && result.enrichedPeople.length > 0) {
        const enrichedPerson = result.enrichedPeople[0];

        if (enrichedPerson.email && isValidEmail(enrichedPerson.email)) {
          alert(`Email found: ${enrichedPerson.email}`);
          // Refresh the preview leads to show the updated email
          fetchPreviewLeads();
        } else {
          alert('No email address could be found for this contact.');
        }
      } else {
        const errorMessage = result.error || 'Email enrichment failed';
        alert(`Failed to enrich email: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Email enrichment error:', error);
      alert('Failed to enrich email: Network or server error');
    }
  };

  useEffect(() => {
    fetchPreviewLeads();
    loadTemplates();
  }, [fetchPreviewLeads]);

  // Load available email templates
  const loadTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const response = await fetch('/api/email-templates', {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTemplates(result.templates || []);
        }
      }
    } catch (error) {
      console.error("Failed to load templates:", error);
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  // Helper function to check if email is valid (not a placeholder)
  const isValidEmail = (email) => {
    return email &&
      !email.includes('email_not_unlocked@') &&
      !email.includes('unavailable@');
  };

  // Handle template selection
  const handleTemplateSelection = async (leadId, templateId) => {
    setSelectedTemplates(prev => ({
      ...prev,
      [leadId]: templateId
    }));

    if (templateId) {
      try {
        // Update the backend with the selected template ID
        const response = await fetch(`/api/scraping-preview/update-template/${leadId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            email_template_id: templateId
          })
        });

        if (response.ok) {
          // Update the lead with the template ID
          setPreviewLeads(prev =>
            prev.map(l =>
              l.id === leadId ? {
                ...l,
                email_template_id: parseInt(templateId)
              } : l
            )
          );
        } else {
          const error = await response.json();
          console.error('Failed to update template:', error);
        }
      } catch (error) {
        console.error('Failed to update template:', error);
      }
    } else {
      // Clear template if none selected
      try {
        const response = await fetch(`/api/scraping-preview/update-template/${leadId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            email_template_id: null
          })
        });

        if (response.ok) {
          setPreviewLeads(prev =>
            prev.map(l =>
              l.id === leadId ? {
                ...l,
                email_template_id: null
              } : l
            )
          );
        }
      } catch (error) {
        console.error('Failed to clear template:', error);
      }
    }
  };

  // Get template form data for a specific lead
  const getTemplateFormData = (lead) => {
    const templateId = selectedTemplates[lead.id] || lead.email_template_id;
    if (!templateId) return null;

    const template = templates.find(t => t.id == templateId);
    if (!template) return null;

    const personalizeContent = (content) => {

      // Add form-specific placeholders for auto response templates
      const formReplacements = {
        '[First Name]': lead.first_name || lead.firstName || 'there',
        '{FIRST_NAME}': lead.first_name || lead.firstName || 'there',
        '{LAST_NAME}': lead.last_name || lead.lastName || '',
        '{FULL_NAME}': `${lead.first_name || lead.firstName || ''} ${lead.last_name || lead.lastName || ''} `.trim() || 'there',
        '{EMAIL}': lead.email || '',
        '{COMPANY}': (lead.company && lead.company.trim() !== '') ? lead.company : (lead.organization?.name || 'your company'),
        '{PHONE}': lead.phone || '',
        '{JOB_TITLE}': lead.job_title || lead.title || lead.jobTitle || '',
        '{LOCATION}': lead.city || '',
        '{COUNTRY}': lead.country || '',
      };

      let processedContent = content;

      Object.keys(formReplacements).forEach(placeholder => {
        const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        const beforeReplace = processedContent;
        processedContent = processedContent.replace(regex, formReplacements[placeholder]);

        if (beforeReplace !== processedContent) {
          console.log(`Replaced placeholder "${placeholder}" with "${formReplacements[placeholder]}"`);
          console.log("Content after replacement:", processedContent.substring(0, 40) + "...");
        }
      });

      return processedContent;
    }

    return {
      subject: personalizeContent(template.subject),
      body: personalizeContent(template.body),
      linkPreviewToggle: template.link_previews ? Object.keys(JSON.parse(template.link_previews || '{}')).length > 0 : false
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4" size={32} />
          <p className="text-gray-600">Loading preview leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Scraping Preview</h2>
        <div className="text-sm text-gray-600">
          Total: {pagination.total} •
          Page {pagination.currentPage} of {pagination.totalPages}
        </div>
      </div>

      {/* Search and Bulk Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        {/* Left side - Search Box and Selection Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Selection Control Buttons */}
          {previewLeads.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleSelectAll(true)}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 border border-gray-300"
                title="Select all contacts across all pages"
              >
                Select All
              </button>
              <button
                onClick={() => handleSelectAll(false)}
                className="px-3 py-2 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200 border border-gray-300"
                title="Clear all selections"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Right side - Bulk Actions */}
        {selectedLeads.size > 0 && (
          <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
            <span className="text-sm text-blue-800 font-medium">
              {selectedLeads.size} selected{allSelected ? ' (all leads)' : ''}
            </span>
            <button
              onClick={handleBulkMoveToColdLeads}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 flex items-center gap-1"
              title="Move selected leads to cold leads"
            >
              → Cold
            </button>
          </div>
        )}
      </div>

      {previewLeads.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center">
          <UserCheck className="mx-auto mb-4 text-gray-400" size={48} />
          <h3 className="font-semibold text-gray-900 mb-2">No Preview Leads</h3>
          <p className="text-gray-600">Complete an Apollo search to see leads that need approval.</p>
        </div>
      ) : (
        <>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="text-yellow-600" size={20} />
              <div>
                <h3 className="font-medium text-yellow-800">Review Before Sending</h3>
                <p className="text-sm text-yellow-700">
                  Each email has been personalized. Review and approve before sending.
                  In development mode, all emails sent to zoewangwork@gmail.com.
                </p>
              </div>
            </div>
          </div>

          {/* Table Layout */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 w-12">
                      <input
                        type="checkbox"
                        checked={allSelected || (selectedLeads.size === previewLeads.length && previewLeads.length > 0)}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {previewLeads.map(lead => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      {/* Checkbox Column */}
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedLeads.has(lead.id)}
                          onChange={(e) => handleSelectLead(lead.id, e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>

                      {/* Contact Column */}
                      <td className="px-4 py-3">
                        <div>
                          <div className="font-medium text-gray-900">
                            {lead.first_name} {lead.last_name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {lead.job_title} at {lead.company}
                          </div>
                        </div>
                      </td>

                      {/* Email Column */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {lead.email_status === 'suggested' && isValidEmail(lead.email) ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs">💡</span>
                              <span className="text-sm text-blue-800">{lead.email}</span>
                              <span className="px-1 py-0.5 bg-blue-100 text-blue-800 text-xs rounded">Suggested</span>
                            </div>
                          ) : lead.email_status === 'guess' && isValidEmail(lead.email) ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs">🤔</span>
                              <span className="text-sm text-purple-800">{lead.email}</span>
                              <span className="px-1 py-0.5 bg-purple-100 text-purple-800 text-xs rounded">Guessed</span>
                            </div>
                          ) : (!isValidEmail(lead.email)) ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs">⚠️</span>
                              <span className="text-sm text-orange-800">No Email Available</span>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-900">{lead.email}</span>
                          )}
                        </div>
                      </td>

                      {/* Template Column */}
                      <td className="px-4 py-3">
                        {isValidEmail(lead.email) ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={selectedTemplates[lead.id] || lead.email_template_id || ''}
                              onChange={(e) => handleTemplateSelection(lead.id, e.target.value)}
                              className={`w-32 p-1 border border-gray-300 rounded text-xs truncate ${lead.status !== "pending" ? "bg-gray-100 text-gray-600" : ""
                                }`}
                              disabled={loadingTemplates || lead.status !== "pending"}
                            >
                              <option value="">Select template...</option>
                              {templates.map(template => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </select>

                            {/* Preview button - show when template is selected for any status */}
                            {(selectedTemplates[lead.id] || lead.email_template_id) && (
                              <button
                                onClick={() => setEmailTemplateModal({
                                  isOpen: true,
                                  lead: lead
                                })}
                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200 whitespace-nowrap"
                                title={lead.status === "pending" ? "Preview email template" : "View sent email"}
                              >
                                {lead.status === "pending" ? "Preview" : "View"}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>

                      {/* Status Column */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${lead.status === "approved" ? "bg-green-100 text-green-700" :
                            lead.status === "rejected" ? "bg-red-100 text-red-700" :
                              lead.status === "sent" ? "bg-blue-100 text-blue-700" :
                                lead.status === "delivered" ? "bg-green-100 text-green-700" :
                                  lead.status === "bounced" ? "bg-orange-100 text-orange-700" :
                                    lead.status === "replied" ? "bg-purple-100 text-purple-700" :
                                      "bg-gray-100 text-gray-700"
                            }`}>
                            {lead.status}
                          </span>
                          {/* Show moved status */}
                          {lead.moved_to_cold_leads && (
                            <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                              Moved ({lead.moved_to_cold_leads})
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Actions Column */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {/* Show appropriate button based on email availability */}
                          {isValidEmail(lead.email) ? (
                            <>
                              {/* Move to Cold Leads button - show for all leads that haven't been moved yet */}
                              {!lead.moved_to_cold_leads && (
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await fetch("/api/scraping-preview/move-to-cold-leads", {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                          "Authorization": `Bearer ${localStorage.getItem("token")}`
                                        },
                                        body: JSON.stringify({
                                          previewIds: [lead.id]
                                        })
                                      });

                                      const result = await response.json();

                                      if (response.ok && result.success) {
                                        alert(`Successfully moved ${lead.first_name} ${lead.last_name} to cold leads`);
                                        fetchPreviewLeads(); // Refresh to update the table
                                      } else {
                                        alert(`Failed to move lead: ${result.error || result.message || 'Unknown error'}`);
                                      }
                                    } catch (error) {
                                      console.error("Move to cold leads error:", error);
                                      alert(`Failed to move lead: ${error.message || 'Network or server error'}`);
                                    }
                                  }}
                                  className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 whitespace-nowrap"
                                  title="Move this lead to cold leads table"
                                >
                                  → Cold
                                </button>
                              )}

                              {/* Show Send Email button for pending emails with confirmed emails */}
                              {lead.status === "pending" && isValidEmail(lead.email) && (
                                <button
                                  onClick={async () => {
                                    // Check if Gmail account is connected
                                    if (!gmailStatus?.hasAccounts) {
                                      alert('Please connect a Gmail account before sending emails. Go to Settings to connect your Gmail account.');
                                      return;
                                    }

                                    // Check if template has been selected
                                    if (!selectedTemplates[lead.id] && !lead.email_template_id) {
                                      alert('Please select a template before sending this email.');
                                      return;
                                    }

                                    // Check if valid email address exists
                                    if (!lead.email || !isValidEmail(lead.email)) {
                                      alert('Cannot send email: No valid email address available for this contact.');
                                      return;
                                    }

                                    try {
                                      setSendingLeads(prev => new Set([...prev, lead.id]));
                                      const response = await fetch("/api/scraping-preview/preview/send", {
                                        method: "POST",
                                        headers: {
                                          "Content-Type": "application/json",
                                          "Authorization": `Bearer ${localStorage.getItem("token")}`
                                        },
                                        body: JSON.stringify({
                                          previewIds: [lead.id]
                                        })
                                      });

                                      if (response.ok) {
                                        const result = await response.json();

                                        // Check if the API response indicates success
                                        if (result.success !== false && result.error === undefined) {
                                          alert(`Email sent successfully to ${lead.email}`);
                                          fetchPreviewLeads(); // Refresh to update status
                                        } else {
                                          // API returned success: false or has error field
                                          const errorMessage = result.error || result.message || 'Unknown error occurred';
                                          alert(`Failed to send email: ${errorMessage}`);
                                        }
                                      } else {
                                        // HTTP error status
                                        let errorMessage = 'Unknown error occurred';
                                        try {
                                          const errorData = await response.json();
                                          errorMessage = errorData.error || errorData.message || `Server error (${response.status})`;
                                        } catch (parseError) {
                                          errorMessage = `Server error (${response.status}): ${response.statusText}`;
                                        }
                                        alert(`Failed to send email: ${errorMessage}`);
                                      }
                                    } catch (error) {
                                      console.error("Send error:", error);
                                      alert(`Failed to send email: ${error.message || 'Network or server error'}`);
                                    } finally {
                                      setSendingLeads(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(lead.id);
                                        return newSet;
                                      });
                                    }
                                  }}
                                  disabled={sendingLeads.has(lead.id) || (!selectedTemplates[lead.id] && !lead.email_template_id)}
                                  title={(!selectedTemplates[lead.id] && !lead.email_template_id) ? 'Please select a template before sending' : 'Send email to this lead'}
                                  className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {sendingLeads.has(lead.id) ? "Sending..." : "Send Email"}
                                </button>
                              )}
                            </>
                          ) : (
                            <div className="flex items-center gap-2">

                              {/* Show Enrich Email button when no email and needs enrichment */}
                              {lead.needs_enrichment && (
                                <button
                                  onClick={() => handleEmailEnrichment(lead)}
                                  className="px-2 py-1 bg-orange-600 text-white text-xs rounded hover:bg-orange-700"
                                  title="Get email address for this contact"
                                >
                                  Enrich Email
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Controls */}
          {pagination.totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex gap-1 items-center">
                {/* First page */}
                {currentPage > 3 && pagination.totalPages > 5 && (
                  <>
                    <button
                      onClick={() => setCurrentPage(1)}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      1
                    </button>
                    {currentPage > 4 && <span className="px-2 text-gray-400">...</span>}
                  </>
                )}

                {/* Page numbers around current page */}
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-2 text-sm rounded-lg ${pageNum === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                {/* Last page */}
                {currentPage < pagination.totalPages - 2 && pagination.totalPages > 5 && (
                  <>
                    {currentPage < pagination.totalPages - 3 && <span className="px-2 text-gray-400">...</span>}
                    <button
                      onClick={() => setCurrentPage(pagination.totalPages)}
                      className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      {pagination.totalPages}
                    </button>
                  </>
                )}

                {/* Page input for direct navigation when many pages */}
                {pagination.totalPages > 10 && (
                  <div className="flex items-center gap-2 ml-4">
                    <span className="text-sm text-gray-600">Go to:</span>
                    <input
                      type="number"
                      min="1"
                      max={pagination.totalPages}
                      value={currentPage}
                      onChange={(e) => {
                        const page = parseInt(e.target.value);
                        if (page >= 1 && page <= pagination.totalPages) {
                          setCurrentPage(page);
                        }
                      }}
                      className="w-16 px-2 py-1 text-sm border border-gray-300 rounded"
                    />
                  </div>
                )}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                disabled={currentPage === pagination.totalPages}
                className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Email Template Preview Modal */}
      {emailTemplateModal.isOpen && emailTemplateModal.lead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden mx-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Email Preview - {emailTemplateModal.lead.first_name} {emailTemplateModal.lead.last_name}
                </h3>
                <p className="text-sm text-gray-600">
                  {emailTemplateModal.lead.job_title} at {emailTemplateModal.lead.company}
                </p>
              </div>
              <button
                onClick={() => setEmailTemplateModal({ isOpen: false, lead: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Template Preview using EmailTemplateFormFields */}
              {(selectedTemplates[emailTemplateModal.lead.id] || emailTemplateModal.lead.email_template_id) && getTemplateFormData(emailTemplateModal.lead) && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <EmailTemplateFormFields
                    formData={getTemplateFormData(emailTemplateModal.lead)}
                    onInputChange={() => { }} // Read-only for preview
                    showTemplateHeader={false}
                    showTemplateType={false}
                    linkPreviews={(() => {
                      const templateId = selectedTemplates[emailTemplateModal.lead.id] || emailTemplateModal.lead.email_template_id;
                      const template = templates.find(t => t.id == templateId);
                      if (template?.link_previews) {
                        try {
                          return JSON.parse(template.link_previews);
                        } catch (error) {
                          return {};
                        }
                      }
                      return {};
                    })()}
                    loadingPreview={false}
                    setLinkPreviews={() => { }} // Read-only for preview
                    setLoadingPreview={() => { }} // Read-only for preview
                    attachments={(() => {
                      const templateId = selectedTemplates[emailTemplateModal.lead.id] || emailTemplateModal.lead.email_template_id;
                      const template = templates.find(t => t.id == templateId);
                      if (template?.attachments) {
                        try {
                          return JSON.parse(template.attachments);
                        } catch (error) {
                          return [];
                        }
                      }
                      return [];
                    })()}
                    onFileSelect={() => { }} // Read-only for preview
                    onRemoveAttachment={() => { }} // Read-only for preview
                    compact={false}
                    disabled={false}
                    templateReadOnly={true}
                  />
                </div>
              )}

              {/* Show message if no template selected */}
              {!selectedTemplates[emailTemplateModal.lead.id] && !emailTemplateModal.lead.email_template_id && (
                <div className="text-center py-8 text-gray-500">
                  <div className="mb-2">⚠️ Template Required</div>
                  <div>Please select a template first to preview the email.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
